import { useState, useEffect, Fragment, useMemo, useCallback, useRef } from "react";
import {
  Save,
  X,
  Edit,
  Plus,
  Trash2,
  ChevronDown,
  ChevronUp,
  Upload,
  FileText,
  Info,
} from "lucide-react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "../components/ui/hover-card";
import { writeInvoiceProfileClientToSession } from "../utils/pageTitles";
import { InvoiceConfiguration } from "../components/InvoiceConfiguration";
import { JacketForm } from "../components/JacketForm";
import { BillingProfileConfiguration } from "./BillingProfileConfiguration";
import {
  formatApiDateForDisplay,
  ServiceEndDateCell,
  serviceHasEndDate,
  serviceIsActive,
  serviceIsExpired,
} from "../components/ServiceEndDateCell";
import {
  useCreateClientConfigurationMutation,
  useUpdateClientConfigurationCustomerMutation,
  useGetClientConfigurationCustomerQuery,
  useGetClientConfigurationCustomerJacketJobsQuery,
  useCreateClientConfigurationJacketJobMutation,
  useUpdateClientConfigurationJacketJobMutation,
  useDeleteClientConfigurationJacketJobMutation,
  useGetClientConfigurationCustomerCompanyNumbersQuery,
  useGetClientConfigurationCustomerPoOptionsQuery,
  useGetClientConfigurationCustomerContractsQuery,
  useCreateClientConfigurationContractMutation,
  useUpdateClientConfigurationContractMutation,
  useDeleteClientConfigurationContractMutation,
  useGetClientConfigurationCustomerContractServicesQuery,
  useCreateClientConfigurationContractServiceMutation,
  useUpdateClientConfigurationContractServiceMutation,
  useEndClientConfigurationContractServiceMutation,
  useGetServiceCatalogQuery,
  useGetClientConfigurationGroupDetailsQuery,
  useGetClientConfigurationGroupsQuery,
  useGetClientConfigurationEmployeesQuery,
  useGetClientConfigurationCustgroupAccountExecutivesQuery,
  useGetClientConfigurationCustgroupCustomerTeamOptionsQuery,
  useGetClientConfigurationJobTypesQuery,
  useGetClientConfigurationMasterTypesQuery,
  useGetClientConfigurationSubTypesQuery,
  useGetClientConfigurationJobStatusesQuery,
  useGetClientConfigurationCostCentersQuery,
  useGetClientConfigurationProductsQuery,
  useGetClientConfigurationCustomerInvProfilesQuery,
  selectAuth,
  useAppSelector,
  useGetAuthProfileQuery,
} from "../store";
import { formatContractRateDisplay } from "../utils/formatContractRateDisplay";
import { hasEditClientConfigurationPrivilege } from "../../redux/utils/authPrivileges";

const CLIENT_INFO_FIELDSET_CLASS =
  "m-0 min-w-0 border-0 p-6 disabled:[&_input]:cursor-not-allowed disabled:[&_input]:bg-gray-100 disabled:[&_select]:cursor-not-allowed disabled:[&_select]:bg-gray-100 disabled:[&_textarea]:cursor-not-allowed disabled:[&_textarea]:bg-gray-100 dark:disabled:[&_input]:bg-gray-800 dark:disabled:[&_select]:bg-gray-800 dark:disabled:[&_textarea]:bg-gray-800";

/** {@code act_inv_profile.active_flag}: null/undefined is treated as active (legacy rows). */
function isInvoiceProfileActive(activeFlag) {
  return activeFlag !== false && activeFlag !== "false" && activeFlag !== 0 && activeFlag !== "0";
}

function invoiceProfileStatusLabel(activeFlag) {
  return isInvoiceProfileActive(activeFlag) ? "Active" : "DeActive";
}

function formatClientConfigurationApiError(err) {
  const data = err?.data;
  if (data?.errors && typeof data.errors === "object") {
    return Object.entries(data.errors)
      .map(([field, msg]) => `${field}: ${msg}`)
      .join("\n");
  }
  if (typeof data?.detail === "string" && data.detail) {
    return data.detail;
  }
  return "Save failed. Please try again.";
}

/** Maps API validation keys to UI field keys (e.g. `serviceCode` → `hibbertServiceCode`). */
function normalizeServiceApiFieldErrors(raw) {
  if (!raw || typeof raw !== "object") {
    return {};
  }
  const knownField = new Set([
    "hibbertServiceCode",
    "serviceCode",
    "servicesRendered",
    "rate",
    "effectiveDate",
    "defaultInvoiceDescription",
    "notes",
    "endDate",
    "provider",
    "freightBilling",
    "pricingOption",
    "options",
    "freightPricingAdjustment",
    "freightAdjustmentPercentage",
    "basedOn",
    "category",
    "subCategory",
    "tieredPricingLov",
    "tieredPricing",
    "tieredPricingTiers",
    "startingQty",
    "endingQty",
    "tierRate",
    "_global",
  ]);
  const out = {};
  const unknown = [];
  for (const [k, v] of Object.entries(raw)) {
    const msg = typeof v === "string" ? v : String(v);
    const key =
      k === "serviceCode"
        ? "hibbertServiceCode"
        : k === "freightBilling"
          ? "provider"
          : k === "freightPricingAdjustment" || k === "options"
            ? "pricingOption"
            : k;
    if (knownField.has(key) || k === "serviceCode") {
      out[key] = out[key] ? `${out[key]} ${msg}` : msg;
    } else {
      unknown.push(`${k}: ${msg}`);
    }
  }
  if (unknown.length) {
    const u = unknown.join(" ");
    out._global = out._global ? `${out._global} ${u}` : u;
  }
  return out;
}

/**
 * Returns an error message if the value is not a non-negative currency-style amount, otherwise null.
 * Allows a leading dollar sign, commas as thousands separators, and at most two digits after the decimal point.
 */
function getCurrencyRateValidationError(raw) {
  const trimmed = String(raw ?? "").trim();
  if (!trimmed) {
    return "Please enter a rate.";
  }
  const normalized = trimmed.replace(/^\$\s*/, "").replace(/,/g, "").trim();
  if (!/^\d+(\.\d{1,2})?$/.test(normalized)) {
    return "Enter a valid currency amount with at most two decimal places (e.g. 10.89 or $1,234.56).";
  }
  const n = Number(normalized);
  if (!Number.isFinite(n)) {
    return "Enter a valid currency amount.";
  }
  return null;
}

/** Decimal string for API (strips currency display formatting). */
function contractRateForApi(displayRate) {
  return String(displayRate ?? "").replace(/[$,]/g, "").trim();
}

/** Format tier/client rate for display in form inputs (e.g. $1,234.56). */
function formatTierRateForDisplay(raw) {
  const trimmed = String(raw ?? "").trim();
  if (!trimmed) {
    return "";
  }
  return formatContractRateDisplay(trimmed);
}

const TIERED_PRICING_LOV_NO = "No";
const TIERED_PRICING_LOV_YES = "Yes";

/** Shipping fee catalog codes that show the optional Provider field on Add/Edit Service. */
const SHIPPING_FEE_SERVICE_CODES = new Set([
  "SHP_FEE_SHP",
  "SHP_FEE_GND",
  "SHP_FEE_2DY",
  "SHP_FEE_3DY",
  "SHP_FEE_ON",
  "SHP_FEE_INTL",
  "SHP_FEE_SHP3P",
  "SHP_FEE_SHPDB",
]);

const SHIPPING_PROVIDER_OPTIONS = ["Hibbert", "Direct Bill", "Third Party"];
const SHIPPING_PRICING_OPTION_VALUES = ["Markup", "Discount", "Pass-Through"];

function isShippingPricingOptionWithPercentage(option) {
  const o = String(option ?? "").trim();
  return o === "Markup" || o === "Discount";
}

function sanitizeFreightAdjustmentPercentageInput(raw) {
  const s = String(raw ?? "");
  if (!s) {
    return "";
  }
  let out = "";
  let hasDecimal = false;
  for (const ch of s) {
    if (ch >= "0" && ch <= "9") {
      out += ch;
    } else if (ch === "." && !hasDecimal) {
      hasDecimal = true;
      out += ch;
    }
  }
  return out;
}

function getFreightAdjustmentPercentageValidationError(raw) {
  const trimmed = String(raw ?? "").trim();
  if (!trimmed) {
    return null;
  }
  const normalized = trimmed.replace(/,/g, "");
  if (!/^\d+(\.\d+)?$/.test(normalized)) {
    return "Enter a valid numeric percentage.";
  }
  const n = Number(normalized);
  if (!Number.isFinite(n) || n < 0) {
    return "Enter a valid numeric percentage.";
  }
  return null;
}

function isShippingFeeServiceCode(code) {
  return SHIPPING_FEE_SERVICE_CODES.has(String(code ?? "").trim().toUpperCase());
}

const SERVICE_STATUS_FILTER_ALL = "ALL";
const SERVICE_STATUS_FILTER_ACTIVE = "Active Only";
const SERVICE_STATUS_FILTER_EXPIRED = "Expired Only";
const SERVICE_STATUS_FILTER_OPTIONS = [
  SERVICE_STATUS_FILTER_ALL,
  SERVICE_STATUS_FILTER_ACTIVE,
  SERVICE_STATUS_FILTER_EXPIRED,
];

const BILLING_PROFILE_STATUS_FILTER_ALL = "All";
const BILLING_PROFILE_STATUS_FILTER_ACTIVE = "Active";
const BILLING_PROFILE_STATUS_FILTER_DEACTIVE = "DeActive";
const BILLING_PROFILE_STATUS_FILTER_OPTIONS = [
  BILLING_PROFILE_STATUS_FILTER_ALL,
  BILLING_PROFILE_STATUS_FILTER_ACTIVE,
  BILLING_PROFILE_STATUS_FILTER_DEACTIVE,
];

const BILLING_PROFILE_COMPANY_FILTER_ALL = "All";

function formatBillingProfileCompanyNumberPadded(v) {
  const s = String(v ?? "").trim();
  if (/^\d{1,4}$/.test(s)) {
    return s.padStart(4, "0");
  }
  return s;
}

function getBillingProfileCompanyNumbersFromRow(row) {
  const fromArray = Array.isArray(row.companyNumbers)
    ? row.companyNumbers
    : Array.isArray(row.company_numbers)
      ? row.company_numbers
      : null;
  if (fromArray?.length) {
    return [
      ...new Set(
        fromArray.map((n) => formatBillingProfileCompanyNumberPadded(n)).filter(Boolean)
      ),
    ];
  }
  const legacy = String(row.companyNumber ?? row.company_number ?? "").trim();
  if (!legacy) {
    return [];
  }
  return [
    ...new Set(
      legacy
        .split(",")
        .map((n) => formatBillingProfileCompanyNumberPadded(n.trim()))
        .filter(Boolean)
    ),
  ];
}

function billingProfileMatchesCompanyFilter(profile, companyFilter) {
  if (companyFilter === BILLING_PROFILE_COMPANY_FILTER_ALL) {
    return true;
  }
  const numbers = Array.isArray(profile.companyNumbers) ? profile.companyNumbers : [];
  return numbers.includes(companyFilter);
}

function billingProfileActiveFilterDescription(statusFilter, companyFilter) {
  const parts = [];
  if (statusFilter !== BILLING_PROFILE_STATUS_FILTER_ALL) {
    parts.push(statusFilter);
  }
  if (companyFilter !== BILLING_PROFILE_COMPANY_FILTER_ALL) {
    parts.push(`Company # ${companyFilter}`);
  }
  return parts.join(" / ");
}
/** First tier always starts at quantity 1. */
const FIRST_TIER_STARTING_QTY = "1";

function serviceHasTieredPricing(service) {
  if (!service) {
    return false;
  }
  return (
    service.tieredPricingLov === TIERED_PRICING_LOV_YES ||
    (Array.isArray(service.tieredPricing) && service.tieredPricing.length > 0)
  );
}

function serviceHasExpandableDetails(service) {
  if (!service) {
    return false;
  }
  return (
    Boolean(String(service.notes ?? "").trim()) ||
    serviceHasTieredPricing(service) ||
    isShippingFeeServiceCode(service.hibbertServiceCode)
  );
}

function createEmptyTierPricingRow(startingQty = FIRST_TIER_STARTING_QTY) {
  return {
    id: `tier-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    startingQty: String(startingQty ?? "").trim(),
    endingQty: "",
    tierRate: "",
  };
}

/** Next tier starts at previous ending quantity + 1 (whole numbers only). */
function computeNextTierStartingQty(previousEndingQty) {
  const trimmed = String(previousEndingQty ?? "").trim();
  if (!/^\d+$/.test(trimmed)) {
    return "";
  }
  const endN = Number.parseInt(trimmed, 10);
  return Number.isFinite(endN) ? String(endN + 1) : "";
}

/** Tier 1 starting qty is always 1; tier 2+ starting qty is derived from the prior tier's ending qty. */
function normalizeTierStartingQtyChain(tiers) {
  const list = Array.isArray(tiers) ? [...tiers] : [];
  if (list.length === 0) {
    return list;
  }
  list[0] = { ...list[0], startingQty: FIRST_TIER_STARTING_QTY };
  if (list.length <= 1) {
    return list;
  }
  for (let i = 1; i < list.length; i++) {
    list[i] = {
      ...list[i],
      startingQty: computeNextTierStartingQty(list[i - 1]?.endingQty),
    };
  }
  return list;
}

function cascadeTierStartingQtyAfterEndingChange(tiers, changedIndex) {
  const list = Array.isArray(tiers) ? [...tiers] : [];
  for (let i = Math.max(1, changedIndex + 1); i < list.length; i++) {
    list[i] = {
      ...list[i],
      startingQty: computeNextTierStartingQty(list[i - 1]?.endingQty),
    };
  }
  return list;
}

const INITIAL_CONTRACT_SERVICE_FORM = {
  hibbertServiceCode: "",
  servicesRendered: "",
  basedOn: "",
  defaultInvoiceDescription: "",
  jacketIds: [],
  rate: "",
  effectiveDate: "",
  endDate: "",
  provider: "",
  pricingOption: "",
  freightAdjustmentPercentage: "",
  status: "Active",
  notes: "",
  includeOnInvoice: true,
  tieredPricingLov: TIERED_PRICING_LOV_NO,
  tieredPricingTiers: [createEmptyTierPricingRow()],
};

function createInitialContractServiceForm() {
  return {
    ...INITIAL_CONTRACT_SERVICE_FORM,
    jacketIds: [],
    tieredPricingTiers: [createEmptyTierPricingRow()],
  };
}

function tiersFromSavedPricing(savedTiers) {
  const rows = Array.isArray(savedTiers) ? savedTiers : [];
  if (rows.length === 0) {
    return [createEmptyTierPricingRow()];
  }
  const mapped = rows.map((tier, index) => ({
    id: `tier-saved-${tier.id ?? index}-${index}`,
    startingQty: String(tier.fromVolume ?? "").trim(),
    endingQty: String(tier.toVolume ?? "").trim(),
    tierRate: formatTierRateForDisplay(tier.rate),
  }));
  return normalizeTierStartingQtyChain(mapped);
}

/** @returns {Record<string, string>|null} field errors for one tier row */
function validateSingleTierRowErrors(tier, tierIndex = 0) {
  if (!tier) {
    return null;
  }
  const row =
    tierIndex === 0 ? { ...tier, startingQty: FIRST_TIER_STARTING_QTY } : tier;
  const err = {};
  const startingErr = getTierQtyValidationError(row.startingQty, "Starting Qty");
  if (startingErr) {
    err.startingQty = startingErr;
  }
  const endingErr = getTierQtyValidationError(row.endingQty, "Ending Qty");
  if (endingErr) {
    err.endingQty = endingErr;
  } else if (!err.startingQty) {
    const startN = Number.parseInt(String(row.startingQty).trim(), 10);
    const endN = Number.parseInt(String(row.endingQty).trim(), 10);
    if (Number.isFinite(startN) && Number.isFinite(endN) && endN <= startN) {
      err.endingQty = "Ending Qty must be greater than Starting Qty.";
    }
  }
  const tierRateErr = getCurrencyRateValidationError(row.tierRate);
  if (tierRateErr) {
    err.tierRate = tierRateErr;
  }
  return Object.keys(err).length > 0 ? err : null;
}

const TIER_PRICING_FIELD_FOCUS_ORDER = ["startingQty", "endingQty", "tierRate"];

function focusTierPricingField(tierIndex, field) {
  const el = document.querySelector(
    `[data-tier-field="${field}"][data-tier-index="${tierIndex}"]`
  );
  if (!el || typeof el.focus !== "function") {
    return;
  }
  el.focus({ preventScroll: false });
  if (typeof el.scrollIntoView === "function") {
    el.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }
}

/**
 * Focus the first tier field with an error. On blur, pass {@code preferTierIndex} /
 * {@code preferField} to focus the field the user just left when it is invalid.
 */
function focusFirstTierRowErrors(rowErrors, options = {}) {
  const { preferTierIndex, preferField, limitToIndexes } = options;
  if (!Array.isArray(rowErrors)) {
    return;
  }

  if (preferTierIndex != null && preferField) {
    const preferredRow = rowErrors[preferTierIndex];
    if (preferredRow?.[preferField]) {
      requestAnimationFrame(() => focusTierPricingField(preferTierIndex, preferField));
      return;
    }
  }

  const scanIndexes =
    Array.isArray(limitToIndexes) && limitToIndexes.length > 0
      ? [...new Set(limitToIndexes)].filter((i) => i >= 0 && i < rowErrors.length).sort((a, b) => a - b)
      : rowErrors.map((_, i) => i);

  for (const i of scanIndexes) {
    const row = rowErrors[i];
    if (!row) {
      continue;
    }
    if (row._row) {
      requestAnimationFrame(() => focusTierPricingField(0, "startingQty"));
      return;
    }
    for (const field of TIER_PRICING_FIELD_FOCUS_ORDER) {
      if (row[field] != null) {
        requestAnimationFrame(() => focusTierPricingField(i, field));
        return;
      }
    }
  }

  for (let i = 0; i < rowErrors.length; i++) {
    const row = rowErrors[i];
    if (!row) {
      continue;
    }
    if (row._row) {
      requestAnimationFrame(() => focusTierPricingField(0, "startingQty"));
      return;
    }
    for (const field of TIER_PRICING_FIELD_FOCUS_ORDER) {
      if (row[field] != null) {
        requestAnimationFrame(() => focusTierPricingField(i, field));
        return;
      }
    }
  }
}

/** Re-validates one or more tier rows on blur and updates {@code serviceFieldErrors.tieredPricingTiers}. */
function validateTierFieldsOnBlur(setServiceFieldErrors, tiers, tierIndexes, focusOptions = {}) {
  const list = Array.isArray(tiers) ? tiers : [];
  const indexes = [...new Set(tierIndexes)].filter(
    (i) => Number.isInteger(i) && i >= 0 && i < list.length
  );
  if (indexes.length === 0) {
    return;
  }
  let nextRows = null;
  setServiceFieldErrors((prev) => {
    const prior = Array.isArray(prev.tieredPricingTiers) ? [...prev.tieredPricingTiers] : [];
    nextRows = list.map((tier, i) => {
      if (!indexes.includes(i)) {
        return prior[i] ?? null;
      }
      return validateSingleTierRowErrors(tier, i);
    });
    if (nextRows.every((r) => r == null)) {
      const next = { ...prev };
      delete next.tieredPricingTiers;
      return next;
    }
    return { ...prev, tieredPricingTiers: nextRows };
  });
  if (nextRows?.some(Boolean)) {
    focusFirstTierRowErrors(nextRows, {
      ...focusOptions,
      limitToIndexes: focusOptions.limitToIndexes ?? indexes,
    });
  }
}

/** @returns {Array<Record<string, string>|null>|null} per-row errors, or null if valid */
function validateTieredPricingTierRows(tiers) {
  const list = Array.isArray(tiers) ? tiers : [];
  if (list.length === 0) {
    return [{ _row: "Add at least one tier." }];
  }
  const rowErrors = list.map((tier, i) => validateSingleTierRowErrors(tier, i));
  return rowErrors.some(Boolean) ? rowErrors : null;
}

/**
 * Inserts a tier after {@code tierIndex} only when that row is valid.
 * @returns {boolean} true if a tier was added
 */
function addContractServiceTierAfterIndex(
  setServiceFormData,
  setServiceFieldErrors,
  tiers,
  tierIndex
) {
  const list = Array.isArray(tiers) ? [...tiers] : [];
  if (tierIndex < 0 || tierIndex >= list.length) {
    return false;
  }
  const rowErr = validateSingleTierRowErrors(list[tierIndex], tierIndex);
  if (rowErr) {
    setServiceFieldErrors((prev) => {
      const prior = Array.isArray(prev.tieredPricingTiers) ? [...prev.tieredPricingTiers] : [];
      const nextRows = list.map((_, i) => (i === tierIndex ? rowErr : (prior[i] ?? null)));
      return { ...prev, tieredPricingTiers: nextRows };
    });
    focusFirstTierRowErrors(
      list.map((_, i) => (i === tierIndex ? rowErr : null)),
      { preferTierIndex: tierIndex, limitToIndexes: [tierIndex] }
    );
    return false;
  }
  setServiceFormData((prev) => {
    const nextList = [...(prev.tieredPricingTiers ?? [])];
    const previous = nextList[tierIndex];
    const nextStart = computeNextTierStartingQty(previous?.endingQty);
    nextList.splice(tierIndex + 1, 0, createEmptyTierPricingRow(nextStart));
    return {
      ...prev,
      tieredPricingTiers: normalizeTierStartingQtyChain(nextList),
    };
  });
  return true;
}

function clearTieredPricingTierFieldError(setServiceFieldErrors, tierIndex, field) {
  setServiceFieldErrors((prev) => {
    const rows = prev.tieredPricingTiers;
    if (!Array.isArray(rows) || !rows[tierIndex]?.[field]) {
      return prev;
    }
    const nextRows = rows.map((row, i) => {
      if (i !== tierIndex || !row) {
        return row;
      }
      const nextRow = { ...row };
      delete nextRow[field];
      return Object.keys(nextRow).length > 0 ? nextRow : null;
    });
    if (nextRows.every((r) => r == null)) {
      const next = { ...prev };
      delete next.tieredPricingTiers;
      return next;
    }
    return { ...prev, tieredPricingTiers: nextRows };
  });
}

/** Returns an error message if the value is not a positive whole number, otherwise null. */
function getTierQtyValidationError(raw, label) {
  const trimmed = String(raw ?? "").trim();
  if (!trimmed) {
    return `Please enter ${label}.`;
  }
  if (!/^\d+$/.test(trimmed)) {
    return `${label} must be a whole number.`;
  }
  return null;
}

function mapContractTieredPricingFromApi(rawTiers) {
  if (!Array.isArray(rawTiers)) {
    return [];
  }
  return rawTiers.map((tier, index) => ({
    id: tier.contractTierpricingId ?? tier.contract_tierpricing_id ?? index + 1,
    fromVolume: String(tier.qtyStart ?? tier.qty_start ?? "").trim(),
    toVolume: String(tier.qtyEnd ?? tier.qty_end ?? "").trim(),
    rate:
      tier.tierClientCost != null && tier.tierClientCost !== ""
        ? String(tier.tierClientCost)
        : String(tier.tierclientcost ?? "").trim(),
  }));
}

/** Request body tiers for act_contract_tierpricing insert. */
function buildContractServiceTieredPricingSaveBody(form) {
  if (form.tieredPricingLov !== TIERED_PRICING_LOV_YES) {
    return [];
  }
  return (form.tieredPricingTiers ?? [])
    .map((tier, index) => ({
      qtyStart:
        index === 0
          ? FIRST_TIER_STARTING_QTY
          : String(tier.startingQty ?? "").trim(),
      qtyEnd: String(tier.endingQty ?? "").trim(),
      rate: contractRateForApi(tier.tierRate),
    }))
    .filter((t) => t.qtyStart || t.qtyEnd || t.rate);
}

function formatJacketJobsLoadError(err) {
  if (!err) {
    return "Unable to load jacket jobs.";
  }
  const data = err?.data;
  if (typeof data?.detail === "string" && data.detail.trim()) {
    return data.detail.trim();
  }
  if (typeof err?.error === "string" && err.error.trim()) {
    return err.error.trim();
  }
  if (err?.status === "FETCH_ERROR") {
    return "Network error loading jacket jobs. Check that the API server is running.";
  }
  if (typeof err?.status === "number") {
    return `Unable to load jacket jobs (HTTP ${err.status}).`;
  }
  return "Unable to load jacket jobs.";
}

const INITIAL_JACKET_FORM = {
  clientName: "",
  customerNumber: "",
  jobNumber: "",
  startDate: "",
  endDate: "",
  poId: "",
  billingProfileId: "",
  status: "",
  jobType: "",
  type: "",
  subType: "",
  cycle: "",
  group: "",
  programId: "",
  rush: false,
  customer: "",
  addressLine1: "",
  addressLine2: "",
  addressLine3: "",
  city: "",
  state: "",
  zip: "",
  attention: "",
  orgDate: "",
  openDate: "",
  requestedDueDate: "",
  mustHaveDueDate: "",
  fromDate: "",
  toDate: "",
  actualShipDate: "",
  revisedDate: "",
  product: "",
  fulfillment: "",
  costCenter: "",
  adminCharge: "",
  markup: "",
  shipSurcharge: "",
  rushSurcharge: "",
  totalHours: "",
  accountExecutive: "",
  billCoordinator: "",
  csr: "",
  teamManager: "",
  hibbertStock: false,
  jobHibbert: false,
  outsidePurchase: false,
  market: "",
  rework: "",
  billable: false,
  costCode: "",
  estimateNumber: "",
  previousJob: "",
  companyNumber: "",
  description1: "",
  description2: "",
  notes: "",
  billingInfo: "",
};

/** Contract version dropdown: V1.0, V1.1, … V10.0 (step 0.1) */
const CONTRACT_VERSION_SELECT_OPTIONS = Array.from({ length: 91 }, (_, i) => {
  const val = (10 + i) / 10;
  return `V${val.toFixed(1)}`;
});

/** Maps to {@code act_contract.billing_cycle}. */
const CONTRACT_BILLING_CYCLE_OPTIONS = [
  { value: "05", label: "5th Billing Cycle" },
  { value: "15", label: "15th Billing Cycle" },
  { value: "20", label: "20th Billing Cycle" },
  { value: "25", label: "25th Billing Cycle" },
  { value: "30", label: "EOM Billing Cycle" },
];

const DEFAULT_CONTRACT_BILLING_CYCLE = "30";

/** Maps to {@code act_contract.net_terms}. */
const CONTRACT_NET_TERMS_OPTIONS = [
  { value: 30, label: "Net 30" },
  { value: 60, label: "Net 60" },
  { value: 90, label: "Net 90" },
];

const CONTRACT_BILLING_CYCLE_LABEL_TO_VALUE = Object.fromEntries(
  CONTRACT_BILLING_CYCLE_OPTIONS.map((o) => [o.label, o.value])
);

const CONTRACT_NET_TERMS_LABEL_TO_VALUE = Object.fromEntries(
  CONTRACT_NET_TERMS_OPTIONS.map((o) => [o.label, o.value])
);

function normalizeContractBillingCycleForSelect(raw) {
  if (raw == null || raw === "") {
    return DEFAULT_CONTRACT_BILLING_CYCLE;
  }
  const trimmed = String(raw).trim();
  if (CONTRACT_BILLING_CYCLE_OPTIONS.some((o) => o.value === trimmed)) {
    return trimmed;
  }
  const n = parseInt(trimmed, 10);
  const asString = Number.isFinite(n) ? String(n) : "";
  if (CONTRACT_BILLING_CYCLE_OPTIONS.some((o) => o.value === asString)) {
    return asString;
  }
  const padded = Number.isFinite(n) && n >= 0 && n < 10 ? String(n).padStart(2, "0") : "";
  if (padded && CONTRACT_BILLING_CYCLE_OPTIONS.some((o) => o.value === padded)) {
    return padded;
  }
  const fromLabel = CONTRACT_BILLING_CYCLE_LABEL_TO_VALUE[trimmed];
  if (fromLabel != null) {
    return String(fromLabel);
  }
  const legacyJacketCodes = { 3: "15", 4: "20", 5: "25", 6: "30" };
  if (legacyJacketCodes[n] != null) {
    return legacyJacketCodes[n];
  }
  const legacyShort = { "5th": "15", "25th": "25", EOM: "30" };
  if (legacyShort[trimmed] != null) {
    return legacyShort[trimmed];
  }
  return DEFAULT_CONTRACT_BILLING_CYCLE;
}

function normalizeContractNetTermsForSelect(raw) {
  if (raw == null || raw === "") {
    return CONTRACT_NET_TERMS_OPTIONS[0].value;
  }
  const n = typeof raw === "number" ? raw : parseInt(String(raw).trim(), 10);
  if (CONTRACT_NET_TERMS_OPTIONS.some((o) => o.value === n)) {
    return n;
  }
  const fromLabel = CONTRACT_NET_TERMS_LABEL_TO_VALUE[String(raw).trim()];
  if (fromLabel != null) {
    return fromLabel;
  }
  return CONTRACT_NET_TERMS_OPTIONS[0].value;
}

function normalizeContractVersionForSelect(raw) {
  const v = String(raw ?? "").trim();
  if (CONTRACT_VERSION_SELECT_OPTIONS.includes(v)) {
    return v;
  }
  const stripped = v.replace(/^v/i, "").trim();
  const n = parseFloat(stripped);
  if (!Number.isFinite(n) || n < 1 || n > 10) {
    return CONTRACT_VERSION_SELECT_OPTIONS[0];
  }
  const snapped = Math.min(10, Math.max(1, Math.round(n * 10) / 10));
  const label = `V${snapped.toFixed(1)}`;
  if (CONTRACT_VERSION_SELECT_OPTIONS.includes(label)) {
    return label;
  }
  return CONTRACT_VERSION_SELECT_OPTIONS[0];
}

/** Fields compared when enabling Update Contract (matches save payload). */
function buildContractFormSaveComparable(formData) {
  return {
    contractName: (formData.contractName ?? "").trim(),
    subContract: (formData.subContract ?? "").trim(),
    contractVersion: (formData.contractVersion ?? "").trim(),
    effectiveDate: formData.effectiveDate ?? "",
    endDate: (formData.endDate ?? "").trim(),
    status: formData.status ?? "",
    billingCycle: normalizeContractBillingCycleForSelect(formData.billingCycle),
    netTerms: normalizeContractNetTermsForSelect(formData.netTerms),
    notes: formData.notes ?? "",
  };
}

/** Fields compared when enabling Update Service (matches save payload). */
function buildServiceFormSaveComparable(form, categoryFilter, subCategoryFilter) {
  const tieredLov = form.tieredPricingLov ?? TIERED_PRICING_LOV_NO;
  const basedOn = String(form.basedOn ?? "").trim();
  return {
    category: (categoryFilter ?? "").trim(),
    subCategory: (subCategoryFilter ?? "").trim(),
    serviceCode: (form.hibbertServiceCode ?? "").trim(),
    servicesRendered: (form.servicesRendered ?? "").trim(),
    basedOn: basedOn || null,
    defaultInvoiceDescription: (form.defaultInvoiceDescription ?? "").trim(),
    rate: tieredLov === TIERED_PRICING_LOV_YES ? "0" : (form.rate ?? "").trim(),
    effectiveDate: form.effectiveDate ?? "",
    endDate: (form.endDate ?? "").trim(),
    provider: (form.provider ?? "").trim(),
    pricingOption: (form.pricingOption ?? "").trim(),
    freightAdjustmentPercentage: (form.freightAdjustmentPercentage ?? "").trim(),
    notes: (form.notes ?? "").trim(),
    tieredPricingLov: tieredLov,
    tieredPricing: buildContractServiceTieredPricingSaveBody(form),
  };
}

function mapDbJobToJacketCard(job, clientName, customerNumberStr) {
  const j = job ?? {};
  const jobNo = j.jobNo ?? j.jobNumber ?? "";
  return {
    ...INITIAL_JACKET_FORM,
    id: jobNo,
    jobNumber: jobNo,
    clientName: clientName ?? "",
    customerNumber: customerNumberStr ?? "",
    jobType: j.jobType ?? "",
    type: j.type ?? "",
    subType: j.subType ?? "",
    status: j.status ?? "",
    cycle: j.cycle != null && j.cycle !== "" ? String(j.cycle) : "",
    group: j.group ?? "",
    programId: j.programId ?? "",
    mustHaveDueDate: j.mustHaveDueDate ?? "",
    product: j.product ?? "",
    costCenter: j.costCenter ?? "",
    billable: j.billable === true,
    accountExecutive: j.accountExecutive != null ? String(j.accountExecutive) : "",
    billCoordinator: j.billCoordinator != null ? String(j.billCoordinator) : "",
    csr: j.csr != null ? String(j.csr) : "",
    teamManager: j.teamManager != null ? String(j.teamManager) : "",
    costCode: j.costCode ?? "",
    estimateNumber: j.estimateNumber ?? "",
    previousJob: j.previousJob ?? "",
    companyNumber: j.companyNumber ?? "",
    description1: j.description1 ?? j.jobdesc1 ?? "",
    description2: j.description2 ?? j.jobdesc2 ?? "",
    notes: j.notes ?? "",
    billingInfo: j.billingInfo ?? "",
  };
}

function buildJacketSaveRequestBody(formData) {
  return {
    jobType: formData.jobType ?? "",
    type: formData.type ?? "",
    subType: formData.subType ?? "",
    status: formData.status ?? "",
    cycle: formData.cycle ?? "",
    group: formData.group ?? "",
    programId: formData.programId ?? "",
    mustHaveDueDate: formData.mustHaveDueDate ?? "",
    product: formData.product ?? "",
    costCenter: formData.costCenter ?? "",
    billable: formData.billable === true,
    accountExecutive: formData.accountExecutive ?? "",
    billCoordinator: formData.billCoordinator ?? "",
    csr: formData.csr ?? "",
    teamManager: formData.teamManager ?? "",
    costCode: formData.costCode ?? "",
    poId: formData.poId ?? "",
    estimateNumber: formData.estimateNumber ?? "",
    previousJob: formData.previousJob ?? "",
    companyNumber: formData.companyNumber ?? "",
    description1: formData.description1 ?? "",
    description2: formData.description2 ?? "",
    notes: formData.notes ?? "",
    billingInfo: formData.billingInfo ?? "",
  };
}

function ClientConfiguration() {
  const auth = useAppSelector(selectAuth);
  const { data: authProfile, isSuccess: isAuthProfileLoaded } = useGetAuthProfileQuery(undefined, {
    skip: !auth.accessToken,
  });
  const canEditClientConfiguration = hasEditClientConfigurationPrivilege(
    isAuthProfileLoaded ? { privileges: authProfile?.privileges ?? [] } : auth?.decodedToken
  );
  const loggedInEmployeeIdForJacket = useMemo(() => {
    const sub = auth?.decodedToken?.sub;
    if (sub == null) {
      return "";
    }
    const s = String(sub).trim();
    return /^\d+$/.test(s) ? s : "";
  }, [auth?.decodedToken?.sub]);
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isNewClient = id === "new";
  const custIdForQuery = useMemo(() => {
    if (isNewClient) {
      return null;
    }
    const fromQuery = Number.parseInt(searchParams.get("custId") ?? "", 10);
    if (Number.isFinite(fromQuery) && fromQuery > 0) {
      return fromQuery;
    }
    if (!id) {
      return null;
    }
    const fromPath = Number.parseInt(id, 10);
    return Number.isFinite(fromPath) && fromPath > 0 ? fromPath : null;
  }, [id, isNewClient, searchParams]);
  const hasInvalidClientRoute = !isNewClient && Boolean(id) && custIdForQuery == null;
  const {
    data: serverCustomer,
    isSuccess: isCustomerDetailSuccess,
    isError: isCustomerDetailError,
  } = useGetClientConfigurationCustomerQuery(custIdForQuery, {
    skip: custIdForQuery == null,
  });
  const {
    data: jacketJobsPayload,
    isLoading: isJacketJobsLoading,
    isSuccess: isJacketJobsSuccess,
    isError: isJacketJobsError,
    error: jacketJobsError,
  } = useGetClientConfigurationCustomerJacketJobsQuery(custIdForQuery, {
    skip: custIdForQuery == null,
  });
  const {
    data: companyNumberOptions = [],
    isLoading: isCompanyNumbersLoading,
    isError: isCompanyNumbersError,
  } = useGetClientConfigurationCustomerCompanyNumbersQuery(custIdForQuery, {
    skip: custIdForQuery == null,
  });
  const {
    data: poDropdownOptions = [],
    isLoading: isPoDropdownLoading,
    isError: isPoDropdownError,
  } = useGetClientConfigurationCustomerPoOptionsQuery(custIdForQuery, {
    skip: custIdForQuery == null,
  });
  const {
    data: contracts = [],
    isFetching: isContractsLoading,
    isError: isContractsError,
  } = useGetClientConfigurationCustomerContractsQuery(custIdForQuery, {
    skip: custIdForQuery == null,
  });
  const {
    data: invProfilesFromApi = [],
    isSuccess: isInvProfilesLoaded,
    isFetching: isInvProfilesFetching,
  } = useGetClientConfigurationCustomerInvProfilesQuery(custIdForQuery, {
    skip: custIdForQuery == null,
  });
  const jacketJobs = useMemo(() => jacketJobsPayload?.jobs ?? [], [jacketJobsPayload]);
  const validTabIds = [
    "client-info",
    "jackets",
    "contracts",
    "billing-profiles",
    "invoices",
    "invoice-config",
  ];
  const tabFromUrl = searchParams.get("tab");
  const jobNoFromUrl = searchParams.get("jobNo");
  const activeTab =
    tabFromUrl && validTabIds.includes(tabFromUrl) ? tabFromUrl : "client-info";

  const setActiveTab = (tabId) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("tab", tabId);
        return next;
      },
      { replace: true }
    );
  };
  const [showPOModal, setShowPOModal] = useState(false);
  const [showClientUpdatedPopup, setShowClientUpdatedPopup] = useState(false);
  const [poFormData, setPoFormData] = useState({
    poNumber: "",
    startDate: "",
    endDate: "",
    status: "Active",
    amount: ""
  });
  const [, setPurchaseOrders] = useState([]);
  const [showJacketModal, setShowJacketModal] = useState(false);
  const [editingJacket, setEditingJacket] = useState(null);
  const [jacketFormData, setJacketFormData] = useState(() => ({ ...INITIAL_JACKET_FORM }));
  const [editingBillingProfileId, setEditingBillingProfileId] = useState(null);
  const [showCreateInvoiceProfileModal, setShowCreateInvoiceProfileModal] = useState(false);
  const [billingProfileStatusFilter, setBillingProfileStatusFilter] = useState(
    BILLING_PROFILE_STATUS_FILTER_ALL
  );
  const [billingProfileCompanyFilter, setBillingProfileCompanyFilter] = useState(
    BILLING_PROFILE_COMPANY_FILTER_ALL
  );
  const [expandedServiceId, setExpandedServiceId] = useState(null);
  const [servicesStatusFilter, setServicesStatusFilter] = useState(SERVICE_STATUS_FILTER_ALL);
  const [showAddServiceDrawer, setShowAddServiceDrawer] = useState(false);
  const [editingServiceId, setEditingServiceId] = useState(null);
  const [editingContract, setEditingContract] = useState(null);
  const [showNewContractPanel, setShowNewContractPanel] = useState(false);
  /** 'upload' | 'update' when the contract action unavailable shadow box is open */
  const [contractActionUnavailable, setContractActionUnavailable] = useState(null);

  const CONTRACT_ACTION_UNAVAILABLE_COPY = {
    upload: {
      title: "Upload Contract",
      message:
        '"The Upload Contract" is not available in this version. Stay tuned.',
    },
  };
  const [contractFieldErrors, setContractFieldErrors] = useState({});
  const [contractSaveSuccessBanner, setContractSaveSuccessBanner] = useState(null);
  const [serviceFieldErrors, setServiceFieldErrors] = useState({});
  const [serviceSaveSuccessBanner, setServiceSaveSuccessBanner] = useState(null);
  const [serviceCatalogCategoryFilter, setServiceCatalogCategoryFilter] = useState("");
  const [serviceCatalogSubCategoryFilter, setServiceCatalogSubCategoryFilter] = useState("");
  const serviceCatalogEditFiltersSyncedRef = useRef(false);
  const contractNameInputRef = useRef(null);
  const contractEffectiveDateInputRef = useRef(null);
  const billingProfileEditPanelRef = useRef(null);
  const contractEditBaselineRef = useRef(null);
  const serviceEditBaselineRef = useRef(null);
  const serviceHibbertCodeSelectRef = useRef(null);
  const serviceServicesRenderedRef = useRef(null);
  const serviceRateInputRef = useRef(null);
  const serviceEffectiveDateInputRef = useRef(null);
  const serviceNotesRef = useRef(null);
  const serviceBasedOnRef = useRef(null);
  const serviceCatalogCategorySelectRef = useRef(null);
  const serviceCatalogSubCategorySelectRef = useRef(null);
  const serviceTieredPricingLovRef = useRef(null);
  const serviceProviderSelectRef = useRef(null);
  const servicePricingOptionSelectRef = useRef(null);
  const serviceFreightAdjustmentPercentageRef = useRef(null);
  const [contractFormData, setContractFormData] = useState({
    clientName: "",
    contractName: "",
    subContract: "",
    contractVersion: "V1.0",
    effectiveDate: "",
    endDate: "",
    status: "Draft",
    billingCycle: DEFAULT_CONTRACT_BILLING_CYCLE,
    netTerms: 30,
    uploadedDocument: "",
    notes: "",
    assignedJackets: [],
  });
  const [serviceFormData, setServiceFormData] = useState(createInitialContractServiceForm);
  const [invoiceLines, setInvoiceLines] = useState([]);
  const [createClientConfiguration, { isLoading: isSavingNewClient }] =
    useCreateClientConfigurationMutation();
  const [updateClientConfigurationCustomer, { isLoading: isSavingExistingClient }] =
    useUpdateClientConfigurationCustomerMutation();
  const [createJacketJob] = useCreateClientConfigurationJacketJobMutation();
  const [updateJacketJob] = useUpdateClientConfigurationJacketJobMutation();
  const [deleteJacketJob] = useDeleteClientConfigurationJacketJobMutation();
  const [createContract, { isLoading: isCreatingContract }] =
    useCreateClientConfigurationContractMutation();
  const [updateContract, { isLoading: isUpdatingContract }] =
    useUpdateClientConfigurationContractMutation();
  const [deleteContractMutation, { isLoading: isDeletingContract }] =
    useDeleteClientConfigurationContractMutation();
  const [pendingDeleteContractId, setPendingDeleteContractId] = useState(null);
  const [deleteContractError, setDeleteContractError] = useState("");
  const [createContractService, { isLoading: isCreatingContractService }] =
    useCreateClientConfigurationContractServiceMutation();
  const [updateContractService, { isLoading: isUpdatingContractService }] =
    useUpdateClientConfigurationContractServiceMutation();
  const [endContractServiceMutation, { isLoading: isEndingContractService }] =
    useEndClientConfigurationContractServiceMutation();
  const [pendingDeleteServiceId, setPendingDeleteServiceId] = useState(null);
  const [deleteServiceError, setDeleteServiceError] = useState("");
  const contractIdForLoadedServices = useMemo(() => {
    const raw = editingContract?.id ?? editingContract?.contractId;
    if (raw == null || raw === "") {
      return null;
    }
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [editingContract?.id, editingContract?.contractId]);
  const {
    data: contractServicesFromApi = [],
    isFetching: isContractServicesLoading,
    isError: isContractServicesError,
    refetch: refetchContractServices,
  } = useGetClientConfigurationCustomerContractServicesQuery(
    { custId: custIdForQuery, contractId: contractIdForLoadedServices },
    { skip: custIdForQuery == null || contractIdForLoadedServices == null }
  );
  const contractServices = useMemo(
    () =>
      Array.isArray(contractServicesFromApi)
        ? contractServicesFromApi.map((row) => {
            const contractServiceId = row.contractServiceId ?? row.contract_service_id;
            const contractId = row.contractId ?? row.contract_id;
            const serviceCode = row.serviceCode ?? row.service_code ?? "";
            const servicesRendered = row.servicesRendered ?? row.services_rendered ?? "";
            const basedOn = row.basedOn ?? row.based_on;
            const defaultInvoiceDescription =
              row.defaultInvoiceDescription ?? row.default_invoice_description ?? "";
            const clientRate = row.clientRate ?? row.client_rate;
            const notes = row.notes ?? "";
            const effectiveDate = formatApiDateForDisplay(row.effectiveDate ?? row.effective_date);
            const endDate = formatApiDateForDisplay(row.endDate ?? row.end_date);
            const endAppliedByDisplayName = String(
              row.endAppliedByDisplayName ?? row.end_applied_by_display_name ?? ""
            ).trim();
            const category = String(row.category ?? "").trim();
            const subCategory = String(row.subCategory ?? row.sub_category ?? "").trim();
            const tieredPricing = mapContractTieredPricingFromApi(
              row.tieredPricing ?? row.tiered_pricing
            );
            const tieredPricingLovRaw = String(
              row.tieredPricingLov ?? row.tiered_pricing_lov ?? ""
            ).trim();
            const tieredPricingLov =
              tieredPricingLovRaw ||
              (tieredPricing.length > 0 ? TIERED_PRICING_LOV_YES : TIERED_PRICING_LOV_NO);
            return {
              id: contractServiceId,
              contractId,
              category,
              subCategory,
              hibbertServiceCode: serviceCode,
              servicesRendered,
              basedOn: basedOn != null && basedOn !== "" ? String(basedOn) : "",
              defaultInvoiceDescription,
              jacketIds: [],
              rate:
                clientRate !== null && clientRate !== undefined && clientRate !== ""
                  ? String(clientRate)
                  : "",
              effectiveDate,
              endDate,
              endAppliedByDisplayName,
              isEnded: Boolean(endDate),
              status: "Active",
              tieredPricing,
              tieredPricingLov,
              revenueMapping: [],
              invoiceSettings: {
                defaultInvoiceDescription,
                includeOnInvoice: true,
              },
              notes,
              provider: String(row.freightBilling ?? row.freight_billing ?? "").trim(),
              pricingOption: String(
                row.freightPricingAdjustment ?? row.freight_pricing_adjustment ?? ""
              ).trim(),
              freightAdjustmentPercentage: String(
                row.freightAdjustmentPercentage ?? row.freight_adjustment_percentage ?? ""
              ).trim(),
            };
          })
        : [],
    [contractServicesFromApi]
  );

  const filteredContractServices = useMemo(() => {
    if (servicesStatusFilter === SERVICE_STATUS_FILTER_ALL) {
      return contractServices;
    }
    if (servicesStatusFilter === SERVICE_STATUS_FILTER_ACTIVE) {
      return contractServices.filter((service) => serviceIsActive(service));
    }
    if (servicesStatusFilter === SERVICE_STATUS_FILTER_EXPIRED) {
      return contractServices.filter((service) => serviceIsExpired(service));
    }
    return contractServices;
  }, [contractServices, servicesStatusFilter]);

  useEffect(() => {
    setServicesStatusFilter(SERVICE_STATUS_FILTER_ALL);
    setExpandedServiceId(null);
  }, [editingContract?.id, editingContract?.contractId]);

  useEffect(() => {
    if (activeTab !== "contracts") {
      return undefined;
    }
    if (custIdForQuery == null || contractIdForLoadedServices == null) {
      return undefined;
    }
    void refetchContractServices();
    return undefined;
  }, [activeTab, custIdForQuery, contractIdForLoadedServices, refetchContractServices]);

  const {
    data: serviceCatalogRaw = [],
    isFetching: isServiceCatalogLoading,
    isError: isServiceCatalogError,
  } = useGetServiceCatalogQuery(undefined, {
    skip: !showAddServiceDrawer,
  });
  const [groupMode, setGroupMode] = useState("NEW");
  const {
    data: employees = [],
    isLoading: isLoadingEmployees,
    isError: isEmployeeLoadError,
  } = useGetClientConfigurationEmployeesQuery();
  const loggedInPlannerDisplayName = useMemo(() => {
    const id = loggedInEmployeeIdForJacket;
    if (!id) {
      return "";
    }
    const fromList = employees.find((e) => String(e.empId) === id)?.displayName?.trim();
    if (fromList) {
      return fromList;
    }
    const fromJwt = auth?.decodedToken?.name;
    if (typeof fromJwt === "string" && fromJwt.trim()) {
      return fromJwt.trim();
    }
    return "";
  }, [loggedInEmployeeIdForJacket, employees, auth?.decodedToken?.name]);
  const loggedInEmployeeDisplayName = useMemo(() => {
    const given = String(auth?.decodedToken?.given_name ?? "").trim();
    const family = String(auth?.decodedToken?.family_name ?? "").trim();
    if (given || family) {
      return [given, family].filter(Boolean).join(" ");
    }
    const id = loggedInEmployeeIdForJacket;
    if (id) {
      const fromList = employees.find((e) => String(e.empId) === id)?.displayName?.trim();
      if (fromList) {
        return fromList;
      }
    }
    return String(auth?.decodedToken?.name ?? "").trim();
  }, [
    auth?.decodedToken?.given_name,
    auth?.decodedToken?.family_name,
    auth?.decodedToken?.name,
    loggedInEmployeeIdForJacket,
    employees,
  ]);
  const resolveAccountPlannerOrphanLabel = useCallback(
    (empIdStr) => {
      const id = String(empIdStr ?? "").trim();
      if (!id || id !== loggedInEmployeeIdForJacket) {
        return null;
      }
      return loggedInPlannerDisplayName || null;
    },
    [loggedInEmployeeIdForJacket, loggedInPlannerDisplayName]
  );
  const { data: jobTypeOptions = [] } = useGetClientConfigurationJobTypesQuery();
  const { data: jacketMasterTypeOptions = [] } = useGetClientConfigurationMasterTypesQuery();
  const { data: jacketSubTypeOptions = [] } = useGetClientConfigurationSubTypesQuery();
  const { data: jacketJobStatusOptions = [] } = useGetClientConfigurationJobStatusesQuery();
  const { data: costCenterOptions = [] } = useGetClientConfigurationCostCentersQuery();

  useEffect(() => {
    if (!showJacketModal || editingJacket != null) {
      return undefined;
    }
    if (jacketJobStatusOptions.length !== 1) {
      return undefined;
    }
    const onlyId = String(jacketJobStatusOptions[0].jobstatId ?? "").trim();
    if (!onlyId) {
      return undefined;
    }
    setJacketFormData((prev) => {
      if (prev.status != null && String(prev.status).trim() !== "") {
        return prev;
      }
      return { ...prev, status: onlyId };
    });
    return undefined;
  }, [showJacketModal, editingJacket, jacketJobStatusOptions]);
  const { data: productOptions = [] } = useGetClientConfigurationProductsQuery();
  const {
    data: existingGroups = [],
    isLoading: isLoadingGroups,
    isError: isGroupLoadError,
  } = useGetClientConfigurationGroupsQuery(undefined, {
    skip: !isNewClient || groupMode !== "EXISTING",
  });
  const [formData, setFormData] = useState({
    name: "",
    code: "",
    group: "",
    director: "",
    accountExec: "",
    osp: "",
    teamManager: "",
    billingCoordinator: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    zip: "",
    country: "",
    contactName: "",
    email: "",
    phone: "",
    notes: ""
  });
  const jacketsListFromDb = useMemo(() => {
    if (custIdForQuery == null) {
      return [];
    }
    const name = (serverCustomer?.custName ?? formData.name ?? "").trim();
    const custNo = String(custIdForQuery);
    return jacketJobs.map((job) => mapDbJobToJacketCard(job, name, custNo));
  }, [custIdForQuery, jacketJobs, serverCustomer?.custName, formData.name]);
  /** Client name for this configuration page (contracts tab shows it read-only). */
  const contractPageClientName = useMemo(
    () => (serverCustomer?.custName ?? formData.name ?? "").trim(),
    [serverCustomer?.custName, formData.name]
  );

  const serviceCatalogRows = useMemo(() => {
    const arr = Array.isArray(serviceCatalogRaw) ? serviceCatalogRaw : [];
    return arr
      .map((row) => ({
        category: String(row.category ?? "").trim(),
        subCategory: String(row.subCategory ?? "").trim(),
        serviceCode: String(row.serviceCode ?? "").trim(),
        description: String(row.description ?? "").trim(),
        unitOfMeasure: String(row.unitOfMeasure ?? "").trim(),
        active: row.active !== false,
      }))
      .filter((r) => r.serviceCode !== "");
  }, [serviceCatalogRaw]);

  const serviceCatalogCategoryOptions = useMemo(() => {
    const set = new Set();
    for (const r of serviceCatalogRows) {
      if (r.category) {
        set.add(r.category);
      }
    }
    return [...set].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  }, [serviceCatalogRows]);

  const serviceCatalogSubCategoryOptions = useMemo(() => {
    if (!serviceCatalogCategoryFilter) {
      return [];
    }
    const set = new Set();
    for (const r of serviceCatalogRows) {
      if (r.category !== serviceCatalogCategoryFilter) {
        continue;
      }
      if (r.subCategory) {
        set.add(r.subCategory);
      }
    }
    return [...set].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  }, [serviceCatalogRows, serviceCatalogCategoryFilter]);

  const serviceCatalogUnitOfMeasureOptions = useMemo(() => {
    const set = new Set();
    for (const r of serviceCatalogRows) {
      const uom = String(r.unitOfMeasure ?? "").trim();
      if (uom) {
        set.add(uom);
      }
    }
    const current = String(serviceFormData.basedOn ?? "").trim();
    if (current) {
      set.add(current);
    }
    return [...set].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  }, [serviceCatalogRows, serviceFormData.basedOn]);

  const filteredServiceCatalogRows = useMemo(() => {
    return serviceCatalogRows.filter((r) => {
      if (serviceCatalogCategoryFilter && r.category !== serviceCatalogCategoryFilter) {
        return false;
      }
      if (serviceCatalogSubCategoryFilter && r.subCategory !== serviceCatalogSubCategoryFilter) {
        return false;
      }
      return true;
    });
  }, [serviceCatalogRows, serviceCatalogCategoryFilter, serviceCatalogSubCategoryFilter]);

  const orphanServiceCatalogOption = useMemo(() => {
    const code = (serviceFormData.hibbertServiceCode ?? "").trim();
    if (!code) {
      return null;
    }
    if (filteredServiceCatalogRows.some((r) => r.serviceCode === code)) {
      return null;
    }
    const row = serviceCatalogRows.find((r) => r.serviceCode === code);
    const label = row
      ? `${row.serviceCode} — ${row.description || row.serviceCode}`
      : `${code} (not in catalog)`;
    return { code, label };
  }, [serviceFormData.hibbertServiceCode, filteredServiceCatalogRows, serviceCatalogRows]);

  const selectedHibbertServiceDescription = useMemo(() => {
    const code = (serviceFormData.hibbertServiceCode ?? "").trim();
    if (!code) {
      return "";
    }
    const row = serviceCatalogRows.find((r) => r.serviceCode === code);
    return String(row?.description ?? "").trim();
  }, [serviceFormData.hibbertServiceCode, serviceCatalogRows]);

  const focusFirstServiceFieldWithErrors = useCallback((errors) => {
    if (!errors || typeof errors !== "object") {
      return;
    }
    const fieldOrder = [
      "category",
      "subCategory",
      "hibbertServiceCode",
      "provider",
      "pricingOption",
      "freightAdjustmentPercentage",
      "servicesRendered",
      "tieredPricingLov",
      "tieredPricingTiers",
      "rate",
      "effectiveDate",
      "basedOn",
      "notes",
    ];
    for (const key of fieldOrder) {
      if (!errors[key]) {
        continue;
      }
      switch (key) {
        case "category":
          serviceCatalogCategorySelectRef.current?.focus();
          return;
        case "subCategory":
          serviceCatalogSubCategorySelectRef.current?.focus();
          return;
        case "hibbertServiceCode":
          serviceHibbertCodeSelectRef.current?.focus();
          return;
        case "provider":
          serviceProviderSelectRef.current?.focus();
          return;
        case "pricingOption":
          servicePricingOptionSelectRef.current?.focus();
          return;
        case "freightAdjustmentPercentage":
          serviceFreightAdjustmentPercentageRef.current?.focus();
          return;
        case "servicesRendered":
          serviceServicesRenderedRef.current?.focus();
          return;
        case "tieredPricingLov":
          serviceTieredPricingLovRef.current?.focus();
          return;
        case "tieredPricingTiers": {
          focusFirstTierRowErrors(errors.tieredPricingTiers);
          return;
        }
        case "rate":
          serviceRateInputRef.current?.focus();
          return;
        case "effectiveDate":
          serviceEffectiveDateInputRef.current?.focus();
          return;
        case "basedOn":
          serviceBasedOnRef.current?.focus();
          return;
        case "notes":
          serviceNotesRef.current?.focus();
          return;
        default:
          break;
      }
    }
    if (errors._global) {
      serviceHibbertCodeSelectRef.current?.focus();
    }
  }, []);

  useEffect(() => {
    if (!showAddServiceDrawer) {
      serviceCatalogEditFiltersSyncedRef.current = false;
      return undefined;
    }
    if (!editingServiceId) {
      return undefined;
    }
    if (serviceCatalogEditFiltersSyncedRef.current || serviceCatalogRows.length === 0) {
      return undefined;
    }
    const code = (serviceFormData.hibbertServiceCode ?? "").trim();
    if (!code) {
      return undefined;
    }
    const row = serviceCatalogRows.find((r) => r.serviceCode === code);
    if (row) {
      setServiceCatalogCategoryFilter(row.category || "");
      setServiceCatalogSubCategoryFilter(row.subCategory || "");
    }
    serviceCatalogEditFiltersSyncedRef.current = true;
    return undefined;
  }, [
    showAddServiceDrawer,
    editingServiceId,
    serviceCatalogRows,
    serviceFormData.hibbertServiceCode,
  ]);

  useEffect(() => {
    if (!showAddServiceDrawer || isServiceCatalogLoading || serviceCatalogRows.length === 0) {
      return undefined;
    }
    const code = (serviceFormData.hibbertServiceCode ?? "").trim();
    if (!code) {
      return undefined;
    }
    const stillThere = filteredServiceCatalogRows.some((r) => r.serviceCode === code);
    if (stillThere) {
      return undefined;
    }
    setServiceFormData((prev) => ({
      ...prev,
      hibbertServiceCode: "",
      basedOn: "",
      provider: "",
      pricingOption: "",
      freightAdjustmentPercentage: "",
    }));
    return undefined;
  }, [
    showAddServiceDrawer,
    isServiceCatalogLoading,
    serviceCatalogRows.length,
    filteredServiceCatalogRows,
    serviceFormData.hibbertServiceCode,
  ]);

  const custgroupForAccountExec = useMemo(() => {
    const g = (serverCustomer?.custgroup ?? formData.group ?? "").trim();
    return g.length > 0 ? g : null;
  }, [serverCustomer?.custgroup, formData.group]);
  const {
    data: accountExecutiveFromCustgroup = [],
    isLoading: isAccountExecutiveCustgroupLoading,
    isError: isAccountExecutiveCustgroupError,
  } = useGetClientConfigurationCustgroupAccountExecutivesQuery(custgroupForAccountExec, {
    skip: isNewClient || custgroupForAccountExec == null,
  });
  const {
    data: custgroupCustomerTeamOptions,
    isLoading: isCustgroupCustomerTeamOptionsLoading,
    isError: isCustgroupCustomerTeamOptionsError,
  } = useGetClientConfigurationCustgroupCustomerTeamOptionsQuery(custgroupForAccountExec, {
    skip: isNewClient || custgroupForAccountExec == null,
  });
  const teamManagersFromCustgroup = custgroupCustomerTeamOptions?.teamManagers ?? [];
  const billingCoordinatorsFromCustgroup = custgroupCustomerTeamOptions?.billingCoordinators ?? [];
  const isExistingGroupSelection = isNewClient && groupMode === "EXISTING";
  const selectedExistingGroup = (formData.group || "").trim();
  const {
    data: selectedGroupDetails,
    isError: isSelectedGroupDetailsError,
  } = useGetClientConfigurationGroupDetailsQuery(selectedExistingGroup, {
    skip: !isExistingGroupSelection || !selectedExistingGroup,
  });
  const [isPrimaryInfoExpanded, setIsPrimaryInfoExpanded] = useState(true);
  const [isAdditionalInfoExpanded, setIsAdditionalInfoExpanded] = useState(true);
  useEffect(() => {
    if (isNewClient || custIdForQuery == null) {
      return;
    }
    if (isCustomerDetailSuccess && serverCustomer && serverCustomer.custId === custIdForQuery) {
      setFormData({
        name: serverCustomer.custName ?? "",
        code: serverCustomer.clientCode ?? serverCustomer.custgroup ?? "",
        group: serverCustomer.custgroup ?? "",
        director: serverCustomer.director != null ? String(serverCustomer.director) : "",
        accountExec: serverCustomer.accountExec != null ? String(serverCustomer.accountExec) : "",
        osp: serverCustomer.osp ?? "",
        teamManager: serverCustomer.teamManager != null ? String(serverCustomer.teamManager) : "",
        billingCoordinator:
          serverCustomer.billingCoordinator != null ? String(serverCustomer.billingCoordinator) : "",
        addressLine1: serverCustomer.addressLine1 ?? "",
        addressLine2: serverCustomer.addressLine2 ?? "",
        city: serverCustomer.city ?? "",
        state: serverCustomer.state ?? "",
        zip: serverCustomer.zip ?? "",
        country: serverCustomer.country ?? "",
        contactName: serverCustomer.contactName ?? "",
        email: serverCustomer.email ?? "",
        phone: serverCustomer.phone ?? "",
        notes: serverCustomer.notes ?? "",
      });
      setGroupMode("EXISTING");
      return;
    }
    if (!isCustomerDetailError) {
      return;
    }
    const savedClients = localStorage.getItem("clients");
    if (!savedClients) {
      return;
    }
    const clients = JSON.parse(savedClients);
    const client = clients.find((c) => c.id === custIdForQuery);
    if (client) {
      setFormData({
        name: client.name,
        code: client.code,
        group: client.group || "",
        director: client.director || "",
        accountExec: client.accountExec || "",
        osp: client.osp || "",
        teamManager: client.teamManager || "",
        billingCoordinator: client.billingCoordinator || "",
        addressLine1: client.addressLine1 || "",
        addressLine2: client.addressLine2 || "",
        city: client.city || "",
        state: client.state || "",
        zip: client.zip || "",
        country: client.country || "",
        contactName: client.contactName || "",
        email: client.email || "",
        phone: client.phone || "",
        notes: client.notes || "",
      });
      setGroupMode("EXISTING");
    }
  }, [
    isNewClient,
    custIdForQuery,
    serverCustomer,
    isCustomerDetailSuccess,
    isCustomerDetailError,
  ]);
  useEffect(() => {
    if (isNewClient || custIdForQuery == null) {
      return;
    }
    if (!isCustomerDetailSuccess || !serverCustomer || serverCustomer.custId !== custIdForQuery) {
      return;
    }
    writeInvoiceProfileClientToSession({
      custId: custIdForQuery,
      clientName: serverCustomer.custName ?? "",
      clientCode: serverCustomer.clientCode ?? serverCustomer.custgroup ?? "",
    });
  }, [isNewClient, custIdForQuery, isCustomerDetailSuccess, serverCustomer]);
  useEffect(() => {
    if (!isExistingGroupSelection || !selectedGroupDetails) {
      return;
    }
    setFormData((prev) => ({
      ...prev,
      name: selectedGroupDetails.clientName ?? "",
      director:
        selectedGroupDetails.director != null
          ? String(selectedGroupDetails.director)
          : "",
      accountExec:
        selectedGroupDetails.accountExec != null
          ? String(selectedGroupDetails.accountExec)
          : "",
      osp: selectedGroupDetails.osp ?? "",
    }));
  }, [isExistingGroupSelection, selectedGroupDetails]);
  useEffect(() => {
    if (!isExistingGroupSelection || selectedExistingGroup) {
      return;
    }
    setFormData((prev) => ({
      ...prev,
      name: "",
      director: "",
      accountExec: "",
      osp: "",
    }));
  }, [isExistingGroupSelection, selectedExistingGroup]);
  useEffect(() => {
    if (!isNewClient) {
      const savedPOs = localStorage.getItem("purchaseOrders");
      if (savedPOs) {
        const allPOs = JSON.parse(savedPOs);
        const clientPOs = allPOs.filter((po) => po.clientId === parseInt(id || "0", 10));
        setPurchaseOrders(clientPOs);
      }
    }
  }, [id, isNewClient]);
  const billingProfiles = useMemo(() => {
    if (isNewClient || custIdForQuery == null || !isInvProfilesLoaded) {
      return [];
    }
    return invProfilesFromApi.map((row) => {
      const companyNumbers = getBillingProfileCompanyNumbersFromRow(row);
      const company = companyNumbers.join(", ");
      const program = String(row.invoiceNameProgram ?? "").trim();
      const profileName = program || (company ? company : "Invoice profile");
      const activeFlag = row.activeFlag ?? row.active_flag;
      return {
        id: row.invProfileId,
        profileName,
        companyNumber: company,
        companyNumbers,
        invoiceNameProgram: program,
        activeFlag: isInvoiceProfileActive(activeFlag),
        status: invoiceProfileStatusLabel(activeFlag),
      };
    });
  }, [isNewClient, custIdForQuery, isInvProfilesLoaded, invProfilesFromApi]);

  const billingProfileCompanyFilterOptions = useMemo(() => {
    const set = new Set();
    for (const profile of billingProfiles) {
      for (const companyNumber of profile.companyNumbers) {
        if (companyNumber && companyNumber !== "N/A") {
          set.add(companyNumber);
        }
      }
    }
    return [
      BILLING_PROFILE_COMPANY_FILTER_ALL,
      ...[...set].sort((a, b) => a.localeCompare(b, undefined, { numeric: true })),
    ];
  }, [billingProfiles]);

  const filteredBillingProfiles = useMemo(() => {
    let list = billingProfiles;
    if (billingProfileStatusFilter === BILLING_PROFILE_STATUS_FILTER_ACTIVE) {
      list = list.filter((profile) => profile.activeFlag);
    } else if (billingProfileStatusFilter === BILLING_PROFILE_STATUS_FILTER_DEACTIVE) {
      list = list.filter((profile) => !profile.activeFlag);
    }
    if (billingProfileCompanyFilter !== BILLING_PROFILE_COMPANY_FILTER_ALL) {
      list = list.filter((profile) =>
        billingProfileMatchesCompanyFilter(profile, billingProfileCompanyFilter)
      );
    }
    return list;
  }, [billingProfiles, billingProfileStatusFilter, billingProfileCompanyFilter]);

  useEffect(() => {
    if (activeTab !== "billing-profiles") {
      setEditingBillingProfileId(null);
      setShowCreateInvoiceProfileModal(false);
      setBillingProfileStatusFilter(BILLING_PROFILE_STATUS_FILTER_ALL);
      setBillingProfileCompanyFilter(BILLING_PROFILE_COMPANY_FILTER_ALL);
    }
  }, [activeTab]);

  useEffect(() => {
    if (!billingProfileCompanyFilterOptions.includes(billingProfileCompanyFilter)) {
      setBillingProfileCompanyFilter(BILLING_PROFILE_COMPANY_FILTER_ALL);
    }
  }, [billingProfileCompanyFilter, billingProfileCompanyFilterOptions]);

  const handleEditBillingProfile = useCallback((profileId) => {
    setShowCreateInvoiceProfileModal(false);
    setEditingBillingProfileId(profileId);
  }, []);

  useEffect(() => {
    if (editingBillingProfileId == null) {
      return undefined;
    }
    const frameId = requestAnimationFrame(() => {
      billingProfileEditPanelRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
    return () => cancelAnimationFrame(frameId);
  }, [editingBillingProfileId]);
  useEffect(() => {
    if (!isNewClient) {
      const savedLines = localStorage.getItem("invoiceLines");
      if (savedLines) {
        const allLines = JSON.parse(savedLines);
        const clientLines = allLines.filter((line) => line.clientId === parseInt(id || "0"));
        setInvoiceLines(clientLines);
      }
    }
  }, [id, isNewClient]);
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));
  };
  const handleSave = async () => {
    if (!canEditClientConfiguration) {
      return;
    }
    if (!formData.name.trim()) {
      alert("Please enter a client name");
      return;
    }
    if (isNewClient && !formData.group.trim()) {
      alert(groupMode === "EXISTING" ? "Please select an existing group" : "Please enter a group");
      return;
    }
    const savedClients = localStorage.getItem("clients");
    let clients = savedClients ? JSON.parse(savedClients) : [];
    if (isNewClient) {
      let code = (formData.code || "").trim();
      if (!code) {
        const baseCode = formData.name.substring(0, 4).toUpperCase().replace(/[^A-Z]/g, "");
        const prefix = baseCode || "CLNT";
        code = prefix;
        for (let n = 0; n < 10000; n++) {
          const tryCode = n === 0 ? prefix : `${prefix}${n}`;
          if (!clients.some((c) => c.code === tryCode)) {
            code = tryCode;
            break;
          }
        }
      } else {
        const duplicateCode = clients.find((c) => c.code?.toUpperCase() === code.toUpperCase());
        if (duplicateCode) {
          alert("A client with this code already exists. Please use a different code.");
          return;
        }
      }

      const payload = {
        name: formData.name,
        groupMode,
        group: formData.group,
        director: formData.director,
        accountExec: formData.accountExec,
        osp: formData.osp,
        teamManager: formData.teamManager,
        billingCoordinator: formData.billingCoordinator,
        addressLine1: formData.addressLine1,
        addressLine2: formData.addressLine2,
        city: formData.city,
        state: formData.state,
        zip: formData.zip,
        country: formData.country,
        contactName: formData.contactName,
        email: formData.email,
        phone: formData.phone,
        notes: formData.notes,
      };

      try {
        const result = await createClientConfiguration(payload).unwrap();
        const newClient = {
          id: result.custId,
          name: formData.name,
          code,
          group: result.custgroup ?? formData.group ?? "",
          director: formData.director,
          accountExec: formData.accountExec,
          osp: formData.osp,
          teamManager: formData.teamManager,
          billingCoordinator: formData.billingCoordinator,
          status: "Active",
          activePOs: 0,
          activeJackets: 0,
          lastInvoiceDate: "",
          revenueYTD: "$0.00",
          addressLine1: formData.addressLine1,
          addressLine2: formData.addressLine2,
          city: formData.city,
          state: formData.state,
          zip: formData.zip,
          country: formData.country,
          contactName: formData.contactName,
          email: formData.email,
          phone: formData.phone,
          notes: formData.notes,
        };
        clients.push(newClient);
        localStorage.setItem("clients", JSON.stringify(clients));
        alert("Client created successfully!");
        navigate("/clients");
      } catch (err) {
        alert(formatClientConfigurationApiError(err));
      }
      return;
    }

    if (custIdForQuery == null) {
      alert("Save failed. Please try again.");
      return;
    }
    try {
      await updateClientConfigurationCustomer({
        custId: custIdForQuery,
        body: {
          teamManager: formData.teamManager,
          billingCoordinator: formData.billingCoordinator,
          addressLine1: formData.addressLine1,
          addressLine2: formData.addressLine2,
          city: formData.city,
          state: formData.state,
          zip: formData.zip,
          country: formData.country,
          contactName: formData.contactName,
          email: formData.email,
          phone: formData.phone,
          notes: formData.notes,
        },
      }).unwrap();
      clients = clients.map((client) => {
        if (client.id === custIdForQuery) {
          return {
            ...client,
            teamManager: formData.teamManager,
            billingCoordinator: formData.billingCoordinator,
            addressLine1: formData.addressLine1,
            addressLine2: formData.addressLine2,
            city: formData.city,
            state: formData.state,
            zip: formData.zip,
            country: formData.country,
            contactName: formData.contactName,
            email: formData.email,
            phone: formData.phone,
            notes: formData.notes,
          };
        }
        return client;
      });
      localStorage.setItem("clients", JSON.stringify(clients));
      setShowClientUpdatedPopup(true);
    } catch (err) {
      alert(formatClientConfigurationApiError(err));
    }
  };
  const handleDismissClientUpdatedPopup = () => {
    setShowClientUpdatedPopup(false);
  };
  const handleCancel = () => {
    navigate("/clients");
  };
  const handleSavePO = () => {
    const savedPOs = localStorage.getItem("purchaseOrders");
    let allPOs = savedPOs ? JSON.parse(savedPOs) : [];
    const newId = allPOs.length > 0 ? Math.max(...allPOs.map((po) => po.id)) + 1 : 1;
    const newPO = {
      id: newId,
      clientId: parseInt(id || "0"),
      poNumber: poFormData.poNumber,
      startDate: poFormData.startDate,
      endDate: poFormData.endDate,
      status: poFormData.status,
      amount: poFormData.amount
    };
    allPOs.push(newPO);
    localStorage.setItem("purchaseOrders", JSON.stringify(allPOs));
    const clientPOs = allPOs.filter((po) => po.clientId === parseInt(id || "0", 10));
    setPurchaseOrders(clientPOs);
    setShowPOModal(false);
  };
  const handleAddJacket = () => {
    if (!canEditClientConfiguration) {
      return;
    }
    setJacketFormData({
      ...INITIAL_JACKET_FORM,
      clientName: (serverCustomer?.custName ?? formData.name ?? "").trim(),
      customerNumber: custIdForQuery != null ? String(custIdForQuery) : "",
      group: (formData.group ?? "").trim(),
      csr: loggedInEmployeeIdForJacket || "",
    });
    setEditingJacket(null);
    setShowJacketModal(true);
  };
  const handleEditJacket = useCallback((jacket) => {
    setJacketFormData(
      Object.fromEntries(
        Object.keys(INITIAL_JACKET_FORM).map((k) => [k, jacket[k] ?? INITIAL_JACKET_FORM[k]])
      )
    );
    setEditingJacket(jacket);
    setShowJacketModal(true);
  }, []);

  /** Open jacket editor from `/clients/:id?tab=jackets&jobNo=...` (e.g. Clients directory jacket lightbox links). */
  useEffect(() => {
    if (isNewClient || custIdForQuery == null) {
      return undefined;
    }
    if (jobNoFromUrl == null || String(jobNoFromUrl).trim() === "") {
      return undefined;
    }
    if (isJacketJobsError) {
      return undefined;
    }
    if (!isJacketJobsSuccess || isJacketJobsLoading) {
      return undefined;
    }
    let target;
    try {
      target = decodeURIComponent(String(jobNoFromUrl).trim());
    } catch {
      target = String(jobNoFromUrl).trim();
    }
    const job = jacketJobs.find((j) => String(j.jobNo ?? j.jobNumber ?? "").trim() === target);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("jobNo");
        next.set("tab", "jackets");
        return next;
      },
      { replace: true }
    );
    if (job) {
      const name = (serverCustomer?.custName ?? formData.name ?? "").trim();
      handleEditJacket(mapDbJobToJacketCard(job, name, String(custIdForQuery)));
    }
    return undefined;
  }, [
    isNewClient,
    custIdForQuery,
    jobNoFromUrl,
    isJacketJobsSuccess,
    isJacketJobsLoading,
    isJacketJobsError,
    jacketJobs,
    setSearchParams,
    serverCustomer?.custName,
    formData.name,
    handleEditJacket,
  ]);

  const handleSaveJacket = async () => {
    if (!canEditClientConfiguration || custIdForQuery == null) {
      return;
    }
    const body = buildJacketSaveRequestBody(jacketFormData);
    if (editingJacket != null) {
      const jobNo = String(editingJacket.jobNumber ?? editingJacket.id ?? "").trim();
      if (!jobNo) {
        const err = new Error("Field Required");
        throw Object.assign(err, {
          status: 400,
          data: { errors: { jobNo: "Field Required" } },
        });
      }
      await updateJacketJob({ custId: custIdForQuery, jobNo, body }).unwrap();
    } else {
      await createJacketJob({ custId: custIdForQuery, body }).unwrap();
    }
    setShowJacketModal(false);
    setEditingJacket(null);
  };
  const handleDeleteJacket = async (jacketId) => {
    if (!canEditClientConfiguration || custIdForQuery == null) {
      return;
    }
    const jobNo = String(jacketId ?? "").trim();
    if (!jobNo) {
      return;
    }
    try {
      await deleteJacketJob({ custId: custIdForQuery, jobNo }).unwrap();
    } catch (err) {
      alert(formatClientConfigurationApiError(err));
    }
  };
  const handleSaveInvoiceLine = (line) => {
    const savedLines = localStorage.getItem("invoiceLines");
    let allLines = savedLines ? JSON.parse(savedLines) : [];
    const newId = allLines.length > 0 ? Math.max(...allLines.map((l) => l.id)) + 1 : 1;
    const newLine = {
      ...line,
      id: newId,
      clientId: parseInt(id || "0")
    };
    allLines.push(newLine);
    localStorage.setItem("invoiceLines", JSON.stringify(allLines));
    const clientLines = allLines.filter((l) => l.clientId === parseInt(id || "0"));
    setInvoiceLines(clientLines);
  };
  const handleEditInvoiceLine = (line) => {
    const savedLines = localStorage.getItem("invoiceLines");
    let allLines = savedLines ? JSON.parse(savedLines) : [];
    allLines = allLines.map((l) => l.id === line.id ? line : l);
    localStorage.setItem("invoiceLines", JSON.stringify(allLines));
    const clientLines = allLines.filter((l) => l.clientId === parseInt(id || "0"));
    setInvoiceLines(clientLines);
  };
  const handleDeleteInvoiceLine = (lineId) => {
    if (window.confirm("Are you sure you want to delete this invoice line?")) {
      const savedLines = localStorage.getItem("invoiceLines");
      let allLines = savedLines ? JSON.parse(savedLines) : [];
      allLines = allLines.filter((l) => l.id !== lineId);
      localStorage.setItem("invoiceLines", JSON.stringify(allLines));
      setInvoiceLines(invoiceLines.filter((l) => l.id !== lineId));
    }
  };

  const handleSaveContract = async () => {
    const nameErr = !contractFormData.contractName.trim() ? "Please enter a contract name" : "";
    const dateErr = !contractFormData.effectiveDate ? "Please enter an effective date" : "";
    const billingCycleVal = String(contractFormData.billingCycle ?? "").trim();
    const netTermsInt = Number(contractFormData.netTerms);
    const billingCycleErr = !CONTRACT_BILLING_CYCLE_OPTIONS.some((o) => o.value === billingCycleVal)
      ? "Please select a billing cycle"
      : "";
    const netTermsErr = !Number.isInteger(netTermsInt) ? "Please select net terms" : "";
    if (nameErr || dateErr || billingCycleErr || netTermsErr) {
      setContractFieldErrors({
        ...(nameErr ? { contractName: nameErr } : {}),
        ...(dateErr ? { effectiveDate: dateErr } : {}),
        ...(billingCycleErr ? { billingCycle: billingCycleErr } : {}),
        ...(netTermsErr ? { netTerms: netTermsErr } : {}),
      });
      requestAnimationFrame(() => {
        if (nameErr) {
          contractNameInputRef.current?.focus();
        } else if (dateErr) {
          contractEffectiveDateInputRef.current?.focus();
        }
      });
      return;
    }
    setContractFieldErrors({});
    setContractSaveSuccessBanner(null);
    if (custIdForQuery == null) {
      alert("Save contract requires an existing client.");
      return;
    }

    const body = {
      contractName: contractFormData.contractName.trim(),
      subContract: (contractFormData.subContract ?? "").trim(),
      contractVersion: (contractFormData.contractVersion ?? "").trim(),
      effectiveDate: contractFormData.effectiveDate,
      endDate: (contractFormData.endDate ?? "").trim(),
      status: contractFormData.status,
      billingCycle: billingCycleVal,
      netTerms: netTermsInt,
      uploadedDocument: (contractFormData.uploadedDocument ?? "").trim(),
      notes: contractFormData.notes ?? "",
    };
    const wasEditing = editingContract != null;
    const savedContractName = body.contractName;
    try {
      if (wasEditing) {
        const updated = await updateContract({
          custId: custIdForQuery,
          contractId: editingContract.id,
          body,
        }).unwrap();
        setEditingContract(updated);
        const updatedFormData = {
          clientName: contractPageClientName,
          contractName: updated.contractName,
          subContract: updated.subContract || "",
          contractVersion: normalizeContractVersionForSelect(updated.contractVersion),
          effectiveDate: updated.effectiveDate,
          endDate: updated.endDate,
          status: updated.status,
          billingCycle: normalizeContractBillingCycleForSelect(updated.billingCycle),
          netTerms: normalizeContractNetTermsForSelect(updated.netTerms),
          uploadedDocument: updated.uploadedDocument,
          notes: updated.notes,
          assignedJackets: updated.assignedJacketIds || [],
        };
        setContractFormData(updatedFormData);
        contractEditBaselineRef.current = JSON.stringify(
          buildContractFormSaveComparable(updatedFormData)
        );
        setContractSaveSuccessBanner(`${savedContractName} contract updated successfully!`);
        return;
      }

      await createContract({ custId: custIdForQuery, body }).unwrap();

      setContractFormData({
        clientName: contractPageClientName,
        contractName: "",
        subContract: "",
        contractVersion: "V1.0",
        effectiveDate: "",
        endDate: "",
        status: "Draft",
        billingCycle: DEFAULT_CONTRACT_BILLING_CYCLE,
        netTerms: 30,
        uploadedDocument: "",
        notes: "",
        assignedJackets: [],
      });
      setEditingContract(null);
      setShowNewContractPanel(false);

      setContractSaveSuccessBanner(`${savedContractName} contract saved successfully!`);
    } catch (err) {
      setContractSaveSuccessBanner(null);
      const data = err?.data;
      if (data?.errors && typeof data.errors === "object") {
        const mapped = {};
        for (const [k, v] of Object.entries(data.errors)) {
          mapped[k] = typeof v === "string" ? v : String(v);
        }
        setContractFieldErrors(mapped);
        requestAnimationFrame(() => {
          if (mapped.contractName) {
            contractNameInputRef.current?.focus();
          } else if (mapped.effectiveDate) {
            contractEffectiveDateInputRef.current?.focus();
          }
        });
        return;
      }
      alert(formatClientConfigurationApiError(err));
    }
  };

  const handleOpenCreateNewContract = () => {
    setContractFieldErrors({});
    setContractSaveSuccessBanner(null);
    setEditingContract(null);
    setShowNewContractPanel(true);
    setContractFormData({
      clientName: contractPageClientName,
      contractName: "",
      subContract: "",
      contractVersion: "V1.0",
      effectiveDate: "",
      endDate: "",
      status: "Draft",
      billingCycle: DEFAULT_CONTRACT_BILLING_CYCLE,
      netTerms: 30,
      uploadedDocument: "",
      notes: "",
      assignedJackets: [],
    });
  };

  const handleEditContract = (contract) => {
    setContractFieldErrors({});
    setContractSaveSuccessBanner(null);
    setShowNewContractPanel(false);
    const loadedFormData = {
      clientName: contractPageClientName,
      contractName: contract.contractName,
      subContract: contract.subContract || "",
      contractVersion: normalizeContractVersionForSelect(contract.contractVersion),
      effectiveDate: contract.effectiveDate,
      endDate: contract.endDate,
      status: contract.status,
      billingCycle: normalizeContractBillingCycleForSelect(contract.billingCycle),
      netTerms: normalizeContractNetTermsForSelect(contract.netTerms),
      uploadedDocument: contract.uploadedDocument,
      notes: contract.notes,
      assignedJackets: contract.assignedJacketIds || [],
    };
    setContractFormData(loadedFormData);
    contractEditBaselineRef.current = JSON.stringify(
      buildContractFormSaveComparable(loadedFormData)
    );
    setEditingContract(contract);
  };

  const handleDeleteContract = (contractId) => {
    setDeleteContractError("");
    setPendingDeleteContractId(contractId);
  };

  const handleCancelDeleteContract = () => {
    if (isDeletingContract) {
      return;
    }
    setPendingDeleteContractId(null);
    setDeleteContractError("");
  };

  const handleConfirmDeleteContract = async () => {
    if (pendingDeleteContractId == null) {
      return;
    }
    if (custIdForQuery == null) {
      setDeleteContractError("Customer is required to delete a contract.");
      return;
    }
    setDeleteContractError("");
    try {
      await deleteContractMutation({
        custId: custIdForQuery,
        contractId: pendingDeleteContractId,
      }).unwrap();
      if (
        editingContract != null &&
        String(editingContract.id) === String(pendingDeleteContractId)
      ) {
        handleCancelEdit();
      }
      setPendingDeleteContractId(null);
    } catch (err) {
      setDeleteContractError(formatClientConfigurationApiError(err));
    }
  };

  const pendingDeleteContract = useMemo(() => {
    if (pendingDeleteContractId == null) {
      return null;
    }
    return (
      contracts.find((contract) => String(contract.id) === String(pendingDeleteContractId)) ??
      null
    );
  }, [contracts, pendingDeleteContractId]);

  const handleCancelEdit = () => {
    setContractFieldErrors({});
    setContractSaveSuccessBanner(null);
    setContractFormData({
      clientName: contractPageClientName,
      contractName: "",
      subContract: "",
      contractVersion: "V1.0",
      effectiveDate: "",
      endDate: "",
      status: "Draft",
      billingCycle: DEFAULT_CONTRACT_BILLING_CYCLE,
      netTerms: 30,
      uploadedDocument: "",
      notes: "",
      assignedJackets: [],
    });
    setEditingContract(null);
    setShowNewContractPanel(false);
    contractEditBaselineRef.current = null;
  };

  const resetServicePanelForm = useCallback(() => {
    setServiceFieldErrors({});
    setServiceCatalogCategoryFilter("");
    setServiceCatalogSubCategoryFilter("");
    serviceCatalogEditFiltersSyncedRef.current = false;
    setServiceFormData(createInitialContractServiceForm());
    serviceEditBaselineRef.current = null;
  }, []);

  const handleAddService = async () => {
    const nextServiceErrors = {};
    if (!editingContract?.id) {
      nextServiceErrors._global =
        "Save the contract, then open it with Edit before adding services (services are stored per contract in the database).";
    } else if (custIdForQuery == null) {
      nextServiceErrors._global = "Customer must exist before adding contract services.";
    }
    const categoryTrim = (serviceCatalogCategoryFilter ?? "").trim();
    const subCategoryTrim = (serviceCatalogSubCategoryFilter ?? "").trim();
    if (!categoryTrim) {
      nextServiceErrors.category = "Please select a category.";
    } else if (serviceCatalogSubCategoryOptions.length === 0) {
      nextServiceErrors.subCategory =
        "No sub-categories are available for this category in the service catalog.";
    } else if (!subCategoryTrim) {
      nextServiceErrors.subCategory = "Please select a sub-category.";
    }
    if (!serviceFormData.hibbertServiceCode) {
      nextServiceErrors.hibbertServiceCode = "Please select a Hibbert Service Code";
    } else if (
      !isServiceCatalogLoading &&
      serviceCatalogRows.length > 0 &&
      categoryTrim &&
      subCategoryTrim &&
      !filteredServiceCatalogRows.some((r) => r.serviceCode === serviceFormData.hibbertServiceCode.trim())
    ) {
      nextServiceErrors.hibbertServiceCode =
        "Choose a Hibbert Service Code that matches the selected category and sub-category.";
    }
    const servicesRenderedTrim = serviceFormData.servicesRendered.trim();
    if (!servicesRenderedTrim) {
      nextServiceErrors.servicesRendered = "Please enter services rendered";
    } else {
      const servicesRenderedKey = servicesRenderedTrim.toLowerCase();
      const duplicateServicesRendered = contractServices.some((s) => {
        if (editingServiceId != null && String(s.id) === String(editingServiceId)) {
          return false;
        }
        return String(s.servicesRendered ?? "").trim().toLowerCase() === servicesRenderedKey;
      });
      if (duplicateServicesRendered) {
        nextServiceErrors.servicesRendered =
          "Services rendered is already in use on this contract. Enter a unique description.";
      }
    }
    const tieredLov = serviceFormData.tieredPricingLov ?? TIERED_PRICING_LOV_NO;
    if (tieredLov !== TIERED_PRICING_LOV_NO && tieredLov !== TIERED_PRICING_LOV_YES) {
      nextServiceErrors.tieredPricingLov = "Please select Yes or No.";
    }
    if (tieredLov === TIERED_PRICING_LOV_YES) {
      const tierRowErrors = validateTieredPricingTierRows(
        normalizeTierStartingQtyChain(serviceFormData.tieredPricingTiers)
      );
      if (tierRowErrors) {
        nextServiceErrors.tieredPricingTiers = tierRowErrors;
      }
    } else {
      const rateValidationError = getCurrencyRateValidationError(serviceFormData.rate);
      if (rateValidationError) {
        nextServiceErrors.rate = rateValidationError;
      }
    }
    if (!serviceFormData.effectiveDate) {
      nextServiceErrors.effectiveDate = "Please enter an effective date";
    }
    const pricingOptionTrim = String(serviceFormData.pricingOption ?? "").trim();
    const providerTrim = String(serviceFormData.provider ?? "").trim();
    const freightAdjustmentPercentageTrim = String(
      serviceFormData.freightAdjustmentPercentage ?? ""
    ).trim();
    const isShippingFee = isShippingFeeServiceCode(serviceFormData.hibbertServiceCode);
    if (isShippingFee && providerTrim && !pricingOptionTrim) {
      nextServiceErrors.pricingOption = "Please select an option.";
    }
    if (isShippingPricingOptionWithPercentage(pricingOptionTrim)) {
      if (!freightAdjustmentPercentageTrim) {
        nextServiceErrors.freightAdjustmentPercentage =
          "Please enter freight adjustment percentage.";
      } else {
        const pctErr = getFreightAdjustmentPercentageValidationError(freightAdjustmentPercentageTrim);
        if (pctErr) {
          nextServiceErrors.freightAdjustmentPercentage = pctErr;
        }
      }
    }
    if (Object.keys(nextServiceErrors).length > 0) {
      setServiceFieldErrors(nextServiceErrors);
      requestAnimationFrame(() => focusFirstServiceFieldWithErrors(nextServiceErrors));
      return;
    }
    setServiceFieldErrors({});

    const basedOn = String(serviceFormData.basedOn ?? "").trim();
    const serviceCodeTrim = serviceFormData.hibbertServiceCode.trim();
    const body = {
      category: categoryTrim,
      subCategory: subCategoryTrim,
      serviceCode: serviceCodeTrim,
      servicesRendered: servicesRenderedTrim,
      basedOn: basedOn || null,
      defaultInvoiceDescription: (serviceFormData.defaultInvoiceDescription ?? "").trim(),
      rate:
        tieredLov === TIERED_PRICING_LOV_YES ? "0" : serviceFormData.rate.trim(),
      effectiveDate: serviceFormData.effectiveDate,
      endDate: (serviceFormData.endDate ?? "").trim(),
      freightBilling: providerTrim,
      freightPricingAdjustment: pricingOptionTrim,
      freightAdjustmentPercentage: freightAdjustmentPercentageTrim,
      notes: (serviceFormData.notes ?? "").trim(),
      tieredPricingLov: serviceFormData.tieredPricingLov ?? TIERED_PRICING_LOV_NO,
      tieredPricing: buildContractServiceTieredPricingSaveBody(serviceFormData),
    };

    if (editingServiceId) {
      try {
        await updateContractService({
          custId: custIdForQuery,
          contractId: editingContract.id,
          contractServiceId: editingServiceId,
          body,
        }).unwrap();
        handleCloseServiceDrawer();
      } catch (err) {
        const data = err?.data;
        if (data?.errors && typeof data.errors === "object") {
          const mapped = normalizeServiceApiFieldErrors(data.errors);
          setServiceFieldErrors(mapped);
          requestAnimationFrame(() => focusFirstServiceFieldWithErrors(mapped));
          return;
        }
        alert(formatClientConfigurationApiError(err));
      }
      return;
    }

    try {
      await createContractService({
        custId: custIdForQuery,
        contractId: editingContract.id,
        body,
      }).unwrap();
      setServiceSaveSuccessBanner("Service added successfully!");
      resetServicePanelForm();
    } catch (err) {
      const data = err?.data;
      if (data?.errors && typeof data.errors === "object") {
        const mapped = normalizeServiceApiFieldErrors(data.errors);
        setServiceFieldErrors(mapped);
        requestAnimationFrame(() => focusFirstServiceFieldWithErrors(mapped));
        return;
      }
      alert(formatClientConfigurationApiError(err));
    }
  };

  const handleCloseServiceDrawer = () => {
    setServiceSaveSuccessBanner(null);
    resetServicePanelForm();
    setShowAddServiceDrawer(false);
    setEditingServiceId(null);
  };

  const handleEditService = (serviceId) => {
    const service = contractServices.find((s) => s.id === serviceId);
    if (service) {
      const cat = (service.category ?? "").trim();
      const sub = (service.subCategory ?? "").trim();
      const tiers = service.tieredPricing ?? [];
      const hasTiered =
        service.tieredPricingLov === TIERED_PRICING_LOV_YES || tiers.length > 0;
      serviceCatalogEditFiltersSyncedRef.current = cat.length > 0 && sub.length > 0;
      setServiceCatalogCategoryFilter(cat);
      setServiceCatalogSubCategoryFilter(sub);
      setServiceSaveSuccessBanner(null);
      setServiceFieldErrors({});
      const loadedServiceForm = {
        hibbertServiceCode: service.hibbertServiceCode,
        servicesRendered: service.servicesRendered,
        basedOn: service.basedOn,
        defaultInvoiceDescription: service.defaultInvoiceDescription,
        jacketIds: service.jacketIds,
        rate: hasTiered ? "0" : service.rate,
        effectiveDate: service.effectiveDate,
        endDate: service.endDate ?? "",
        provider: service.provider ?? "",
        pricingOption: service.pricingOption ?? "",
        freightAdjustmentPercentage: service.freightAdjustmentPercentage ?? "",
        status: service.status,
        notes: service.notes,
        includeOnInvoice: service.invoiceSettings?.includeOnInvoice ?? true,
        tieredPricingLov:
          service.tieredPricingLov === TIERED_PRICING_LOV_YES || hasTiered
            ? TIERED_PRICING_LOV_YES
            : TIERED_PRICING_LOV_NO,
        tieredPricingTiers: hasTiered
          ? tiersFromSavedPricing(tiers)
          : [createEmptyTierPricingRow()],
      };
      setServiceFormData(loadedServiceForm);
      serviceEditBaselineRef.current = JSON.stringify(
        buildServiceFormSaveComparable(loadedServiceForm, cat, sub)
      );
      setEditingServiceId(serviceId);
      setShowAddServiceDrawer(true);
    }
  };

  const handleDeleteService = (serviceId) => {
    setDeleteServiceError("");
    setPendingDeleteServiceId(serviceId);
  };

  const handleCancelDeleteService = () => {
    if (isEndingContractService) {
      return;
    }
    setPendingDeleteServiceId(null);
    setDeleteServiceError("");
  };

  const handleConfirmDeleteService = async () => {
    if (pendingDeleteServiceId == null) {
      return;
    }
    if (custIdForQuery == null || !editingContract?.id) {
      setDeleteServiceError("Customer and contract are required to end a service.");
      return;
    }
    setDeleteServiceError("");
    try {
      await endContractServiceMutation({
        custId: custIdForQuery,
        contractId: editingContract.id,
        contractServiceId: pendingDeleteServiceId,
      }).unwrap();
      if (expandedServiceId != null && String(expandedServiceId) === String(pendingDeleteServiceId)) {
        setExpandedServiceId(null);
      }
      if (editingServiceId != null && String(editingServiceId) === String(pendingDeleteServiceId)) {
        handleCloseServiceDrawer();
      }
      setPendingDeleteServiceId(null);
    } catch (err) {
      setDeleteServiceError(formatClientConfigurationApiError(err));
    }
  };

  const hasContractFormChanges = useMemo(() => {
    if (editingContract == null) {
      return true;
    }
    const baseline = contractEditBaselineRef.current;
    if (!baseline) {
      return false;
    }
    return (
      JSON.stringify(buildContractFormSaveComparable(contractFormData)) !== baseline
    );
  }, [contractFormData, editingContract]);

  const hasServiceFormChanges = useMemo(() => {
    if (editingServiceId == null) {
      return true;
    }
    const baseline = serviceEditBaselineRef.current;
    if (!baseline) {
      return false;
    }
    return (
      JSON.stringify(
        buildServiceFormSaveComparable(
          serviceFormData,
          serviceCatalogCategoryFilter,
          serviceCatalogSubCategoryFilter
        )
      ) !== baseline
    );
  }, [
    serviceFormData,
    serviceCatalogCategoryFilter,
    serviceCatalogSubCategoryFilter,
    editingServiceId,
  ]);

  const isContractEditorOpen = editingContract != null || showNewContractPanel;

  const tabs = [
    { id: "client-info", label: "Client Info" },
    { id: "jackets", label: "Jackets" },
    { id: "contracts", label: "Contracts" },
    { id: "billing-profiles", label: "Invoice Profile Configuration" },
    // Invoice Configuration tab hidden for now; uncomment to restore
    // { id: "invoice-config", label: "Invoice Configuration" },
  ];
  const configurationPageTitle =
    isNewClient
      ? "Client Configuration"
      : (formData.name || "").trim()
        ? `Client Configuration - ${(formData.name || "").trim()}`
        : "Client Configuration";

  if (hasInvalidClientRoute) {
    return (
      <div className="p-8">
        <h1 className="text-3xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Client Configuration
        </h1>
        <p className="text-sm text-red-600 dark:text-red-400 mb-4">
          Invalid client link. Open a client from the Clients list using View (numeric customer id
          required).
        </p>
        <Link
          to="/clients"
          className="text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
        >
          Back to Clients
        </Link>
      </div>
    );
  }

  return <div className="p-8">
      {
    /* Page Title */
  }
      <div className="mb-8">
        <h1 className="text-3xl font-semibold text-gray-900 dark:text-gray-100">
          {configurationPageTitle}
        </h1>
      </div>

      {
    /* Tab Navigation */
  }
      <div className="mb-6 border-b border-gray-200 dark:border-gray-700">
        <nav className="flex gap-8">
          {tabs.map((tab) => <button
    key={tab.id}
    onClick={() => setActiveTab(tab.id)}
    className={`app-btn-plain pb-4 px-1 border-b-2 transition-colors ${activeTab === tab.id ? "border-blue-600 text-blue-600 dark:text-blue-400" : "border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"}`}
  >
              {tab.label}
            </button>)}
        </nav>
      </div>

      {
    /* Tab Content */
  }
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded shadow-sm">
        {activeTab === "client-info" && <div className="p-8">
            {
    /* Primary Information Section - Collapsible */
  }
            <div className="mb-6 border border-gray-200 dark:border-gray-700 rounded">
              <button
    onClick={() => setIsPrimaryInfoExpanded(!isPrimaryInfoExpanded)}
    className="app-btn-plain w-full flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-750 transition-colors rounded-t text-gray-900 dark:text-gray-100"
  >
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Customer Group</h2>
                {isPrimaryInfoExpanded ? <ChevronUp className="w-5 h-5 text-gray-600 dark:text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-600 dark:text-gray-400" />}
              </button>

              {isPrimaryInfoExpanded && <fieldset disabled={!canEditClientConfiguration} className={CLIENT_INFO_FIELDSET_CLASS}>
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Group
                      </label>
                      {isNewClient ? <>
                          <div className="mb-3 flex gap-4">
                            <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                              <input
    type="radio"
    name="groupMode"
    value="NEW"
    checked={groupMode === "NEW"}
    onChange={(e) => setGroupMode(e.target.value)}
  />
                              New
                            </label>
                            <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                              <input
    type="radio"
    name="groupMode"
    value="EXISTING"
    checked={groupMode === "EXISTING"}
    onChange={(e) => setGroupMode(e.target.value)}
  />
                              Existing
                            </label>
                          </div>
                          {groupMode === "EXISTING" ? <>
                              <select
    value={formData.group}
    onChange={(e) => handleInputChange("group", e.target.value)}
    disabled={isLoadingGroups}
    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
  >
                                <option value="">
                                  {isLoadingGroups ? "Loading groups..." : "Select existing group"}
                                </option>
                                {existingGroups.map((group) => <option key={group} value={group}>
                                    {group}
                                  </option>)}
                              </select>
                              {isGroupLoadError && <p className="mt-2 text-xs text-red-600 dark:text-red-400">
                                  Unable to load existing groups right now.
                                </p>}
                              {isSelectedGroupDetailsError && <p className="mt-2 text-xs text-red-600 dark:text-red-400">
                                  Unable to load selected group details right now.
                                </p>}
                            </> : <input
    type="text"
    value={formData.group}
    onChange={(e) => handleInputChange("group", e.target.value)}
    placeholder="Enter new group"
    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
  />}
                        </> : <input
    type="text"
    value={formData.group}
    onChange={(e) => handleInputChange("group", e.target.value)}
    placeholder="Enter group"
    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
  />}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Client Name *
                      </label>
                      <input
    type="text"
    value={formData.name}
    onChange={(e) => handleInputChange("name", e.target.value)}
    readOnly={!isNewClient || isExistingGroupSelection}
    placeholder="Enter client name"
    className={`w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 text-gray-900 dark:text-gray-100 ${(!isNewClient || isExistingGroupSelection) ? "bg-gray-100 dark:bg-gray-800 cursor-not-allowed" : "bg-white dark:bg-gray-900"}`}
  />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Director
                      </label>
                      <select
    value={formData.director}
    onChange={(e) => handleInputChange("director", e.target.value)}
    disabled={isLoadingEmployees || !isNewClient || isExistingGroupSelection}
    className={`w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 text-gray-900 dark:text-gray-100 ${(!isNewClient || isExistingGroupSelection) ? "bg-gray-100 dark:bg-gray-800 cursor-not-allowed" : "bg-white dark:bg-gray-900"}`}
  >
                        <option value="">
                          {isLoadingEmployees ? "Loading directors..." : "Select director"}
                        </option>
                        {employees.map((employee) => <option key={employee.empId} value={String(employee.empId)}>
                            {employee.displayName}
                          </option>)}
                      </select>
                      {isEmployeeLoadError && <p className="mt-2 text-xs text-red-600 dark:text-red-400">
                          Unable to load employee options right now.
                        </p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Account Exec
                      </label>
                      <select
    value={formData.accountExec}
    onChange={(e) => handleInputChange("accountExec", e.target.value)}
    disabled={isLoadingEmployees || !isNewClient || isExistingGroupSelection}
    className={`w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 text-gray-900 dark:text-gray-100 ${(!isNewClient || isExistingGroupSelection) ? "bg-gray-100 dark:bg-gray-800 cursor-not-allowed" : "bg-white dark:bg-gray-900"}`}
  >
                        <option value="">
                          {isLoadingEmployees ? "Loading account execs..." : "Select account exec"}
                        </option>
                        {employees.map((employee) => <option key={employee.empId} value={String(employee.empId)}>
                            {employee.displayName}
                          </option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        OSP
                      </label>
                      <select
    value={formData.osp}
    onChange={(e) => handleInputChange("osp", e.target.value)}
    disabled={!isNewClient || isExistingGroupSelection}
    className={`w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 text-gray-900 dark:text-gray-100 ${(!isNewClient || isExistingGroupSelection) ? "bg-gray-100 dark:bg-gray-800 cursor-not-allowed" : "bg-white dark:bg-gray-900"}`}
  >
                        <option value="">Select OSP</option>
                        <option value="Yes">Yes</option>
                        <option value="No">No</option>
                      </select>
                    </div>
                  </div>
                </fieldset>}
            </div>

            {
    /* Additional Information Section - Collapsible */
  }
            <div className="mb-6 border border-gray-200 dark:border-gray-700 rounded">
              <button
    onClick={() => setIsAdditionalInfoExpanded(!isAdditionalInfoExpanded)}
    className="app-btn-plain w-full flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-750 transition-colors rounded-t text-gray-900 dark:text-gray-100"
  >
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Additional Information</h2>
                {isAdditionalInfoExpanded ? <ChevronUp className="w-5 h-5 text-gray-600 dark:text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-600 dark:text-gray-400" />}
              </button>

              {isAdditionalInfoExpanded && <fieldset disabled={!canEditClientConfiguration} className={CLIENT_INFO_FIELDSET_CLASS}>
                  {
    /* ID Field - Full Width Row */
  }
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      ID
                    </label>
                    <input
    type="text"
    value={isNewClient ? "Auto-generated on save" : id}
    readOnly
    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 cursor-not-allowed"
  />
                  </div>

                  <div className="grid grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Team Manager
                      </label>
                      <select
    value={formData.teamManager}
    onChange={(e) => handleInputChange("teamManager", e.target.value)}
    disabled={isLoadingEmployees}
    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
  >
                        <option value="">
                          {isLoadingEmployees ? "Loading team managers..." : "Select team manager"}
                        </option>
                        {employees.map((employee) => <option key={employee.empId} value={String(employee.empId)}>
                            {employee.displayName}
                          </option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Billing Coordinator
                      </label>
                      <select
    value={formData.billingCoordinator}
    onChange={(e) => handleInputChange("billingCoordinator", e.target.value)}
    disabled={isLoadingEmployees}
    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
  >
                        <option value="">
                          {isLoadingEmployees ? "Loading billing coordinators..." : "Select billing coordinator"}
                        </option>
                        {employees.map((employee) => <option key={employee.empId} value={String(employee.empId)}>
                            {employee.displayName}
                          </option>)}
                      </select>
                    </div>
                  </div>

                  {
    /* Address Section */
  }
                  <div className="mb-6 pb-6 border-b border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Address</h3>
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Address Line 1
                        </label>
                        <input
    type="text"
    value={formData.addressLine1}
    onChange={(e) => handleInputChange("addressLine1", e.target.value)}
    placeholder="Enter address"
    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
  />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Address Line 2
                        </label>
                        <input
    type="text"
    value={formData.addressLine2}
    onChange={(e) => handleInputChange("addressLine2", e.target.value)}
    placeholder="Suite, unit, etc."
    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
  />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          City
                        </label>
                        <input
    type="text"
    value={formData.city}
    onChange={(e) => handleInputChange("city", e.target.value)}
    placeholder="Enter city"
    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
  />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          State
                        </label>
                        <input
    type="text"
    value={formData.state}
    onChange={(e) => handleInputChange("state", e.target.value)}
    placeholder="Enter state"
    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
  />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Zip
                        </label>
                        <input
    type="text"
    value={formData.zip}
    onChange={(e) => handleInputChange("zip", e.target.value)}
    placeholder="Enter zip code"
    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
  />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Country
                        </label>
                        <input
    type="text"
    value={formData.country}
    onChange={(e) => handleInputChange("country", e.target.value)}
    placeholder="Enter country"
    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
  />
                      </div>
                    </div>
                  </div>

                  {
    /* Primary Contact Section */
  }
                  <div className="mb-6 pb-6 border-b border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Primary Contact</h3>
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Primary Contact Name
                        </label>
                        <input
    type="text"
    value={formData.contactName}
    onChange={(e) => handleInputChange("contactName", e.target.value)}
    placeholder="Enter contact name"
    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
  />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Email
                        </label>
                        <input
    type="email"
    value={formData.email}
    onChange={(e) => handleInputChange("email", e.target.value)}
    placeholder="Enter email address"
    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
  />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Phone
                        </label>
                        <input
    type="tel"
    value={formData.phone}
    onChange={(e) => handleInputChange("phone", e.target.value)}
    placeholder="Enter phone number"
    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
  />
                      </div>
                    </div>
                  </div>

                  {
    /* Notes/Comments Section */
  }
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Notes/Comments:
                    </label>
                    <textarea
    value={formData.notes}
    onChange={(e) => handleInputChange("notes", e.target.value)}
    placeholder="Enter notes or comments"
    rows={4}
    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
  />
                  </div>
                </fieldset>}
            </div>

            {
    /* Action Buttons */
  }
            <div className="flex items-center gap-4 pt-6 border-t border-gray-200 dark:border-gray-700">
              {canEditClientConfiguration ? (
              <button
    className="app-btn-gloss disabled:cursor-not-allowed disabled:opacity-60"
    onClick={handleSave}
    disabled={isSavingNewClient || isSavingExistingClient}
  >
                <Save className="w-4 h-4" />
                Save
              </button>
              ) : null}
              <button className="app-btn-gloss-cancel px-6" onClick={handleCancel}>
                <X className="w-4 h-4" />
                Cancel
              </button>
            </div>
          </div>}

        {activeTab === "jackets" && (
          <div className="p-8">
            <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Jackets</h2>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                  Jobs linked to this customer from act_jobs.
                </p>
              </div>
              {!isNewClient && canEditClientConfiguration ? (
                <button
                  type="button"
                  className="app-btn-gloss shrink-0"
                  onClick={handleAddJacket}
                >
                  <Plus className="h-4 w-4" />
                  Add Jacket
                </button>
              ) : null}
            </div>

            {isNewClient ? (
              <p className="py-12 text-center text-sm text-gray-500 dark:text-gray-400">
                Save the client to view jacket jobs from the database.
              </p>
            ) : (
              <>
                {isJacketJobsError ? (
                  <p className="mb-4 text-sm text-red-600 dark:text-red-400">
                    {formatJacketJobsLoadError(jacketJobsError)}
                  </p>
                ) : null}

                {!isNewClient ? (
                  <JacketForm
                    show={showJacketModal}
                    formData={jacketFormData}
                    editing={editingJacket !== null}
                    readOnly={!canEditClientConfiguration}
                    onSave={handleSaveJacket}
                    onCancel={() => {
                      setShowJacketModal(false);
                      setEditingJacket(null);
                    }}
                    onChange={(field, value) =>
                      setJacketFormData((prev) => ({ ...prev, [field]: value }))
                    }
                    jobTypeOptions={jobTypeOptions}
                    masterTypeOptions={jacketMasterTypeOptions}
                    subTypeOptions={jacketSubTypeOptions}
                    jobStatusOptions={jacketJobStatusOptions}
                    costCenterOptions={costCenterOptions}
                    productOptions={productOptions}
                    jackets={jacketsListFromDb}
                    onEdit={handleEditJacket}
                    onDelete={handleDeleteJacket}
                    employees={employees}
                    isLoadingEmployees={isLoadingEmployees}
                    isEmployeeLoadError={isEmployeeLoadError}
                    accountExecutiveOptions={accountExecutiveFromCustgroup}
                    isLoadingAccountExecutiveOptions={isAccountExecutiveCustgroupLoading}
                    isAccountExecutiveOptionsError={isAccountExecutiveCustgroupError}
                    teamManagerOptionsFromCustomers={teamManagersFromCustgroup}
                    billingCoordinatorOptionsFromCustomers={billingCoordinatorsFromCustgroup}
                    isLoadingCustgroupCustomerTeamOptions={isCustgroupCustomerTeamOptionsLoading}
                    isCustgroupCustomerTeamOptionsError={isCustgroupCustomerTeamOptionsError}
                    companyNumberOptions={companyNumberOptions}
                    isLoadingCompanyNumbers={isCompanyNumbersLoading}
                    isCompanyNumbersLoadError={isCompanyNumbersError}
                    poDropdownOptions={poDropdownOptions}
                    isLoadingPoDropdown={isPoDropdownLoading}
                    isPoDropdownError={isPoDropdownError}
                    resolveAccountPlannerOrphanLabel={resolveAccountPlannerOrphanLabel}
                  />
                ) : null}

              </>
            )}
          </div>
        )}

        {activeTab === "contracts" && (
          <div className="p-8">
            <div className="mb-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-blue-900 dark:text-blue-100">
                Contracts define the source of truth for pricing. Billing Profiles and Invoice
                Configuration will reference these rates.
              </p>
            </div>

            {isContractsError ? (
              <div
                className="mb-6 rounded border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200"
                role="alert"
              >
                Could not load contracts from the server. Try again or contact support if this continues.
              </div>
            ) : null}

            {contractSaveSuccessBanner ? (
              <div
                className="mb-6 rounded border border-green-300 bg-green-50 px-4 py-3 text-sm text-green-900 dark:border-green-800 dark:bg-green-950/40 dark:text-green-100 flex items-start justify-between gap-3"
                role="status"
              >
                <p className="font-medium">{contractSaveSuccessBanner}</p>
                <button
                  type="button"
                  onClick={() => setContractSaveSuccessBanner(null)}
                  className="app-btn-plain p-1 rounded hover:bg-green-100 dark:hover:bg-green-900/50 text-green-800 dark:text-green-200 shrink-0"
                  aria-label="Dismiss success message"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : null}

            {isContractsLoading && contracts.length === 0 && !isContractsError ? (
              <p className="mb-6 text-sm text-gray-600 dark:text-gray-400">Loading contracts…</p>
            ) : null}

            {contracts.length > 0 && (
              <div className="mb-6 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded shadow-sm">
                <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                    Saved Contracts
                  </h2>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 dark:bg-gray-800">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                          Client
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                          Contract Name
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                          Sub Contract
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                          Version
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                          Effective Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                          End Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
                      {contracts.map((contract) => (
                        <tr key={contract.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                          <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                            {contract.clientName || formData.name}
                          </td>
                          <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-gray-100">
                            {contract.contractName}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                            {contract.subContract || "—"}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                            {contract.contractVersion || "—"}
                          </td>
                          <td className="px-6 py-4 text-sm">
                            <span
                              className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                contract.status === "Active"
                                  ? "bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200"
                                  : contract.status === "Draft"
                                    ? "bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200"
                                    : "bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200"
                              }`}
                            >
                              {contract.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                            {contract.effectiveDate}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                            {contract.endDate || "—"}
                          </td>
                          <td className="px-6 py-4 text-sm">
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => handleEditContract(contract)}
                                className="app-btn-plain p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                                title="Edit Contract"
                              >
                                <Edit className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteContract(contract.id)}
                                className="app-btn-plain p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                                title="Delete Contract"
                              >
                                <Trash2 className="w-4 h-4 text-red-600 dark:text-red-400" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="mb-6 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={handleOpenCreateNewContract}
                disabled={custIdForQuery == null}
                className="app-btn-gloss disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Plus className="w-4 h-4" />
                Create New Contract
              </button>
            </div>

            {isContractEditorOpen ? (
              <>
            <div className="mb-6 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded shadow-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                  {editingContract
                    ? `Edit Contract - ${(contractFormData.contractName || editingContract.contractName || "").trim() || "—"}`
                    : "New Contract"}
                </h2>
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="app-btn-gloss-cancel px-4 py-2 text-sm"
                >
                  {editingContract ? "Cancel Edit" : "Close"}
                </button>
              </div>

              <div className="grid grid-cols-3 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Client Name
                  </label>
                  <input
                    type="text"
                    value={contractPageClientName}
                    readOnly
                    aria-readonly="true"
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Contract Name *
                  </label>
                  <input
                    ref={contractNameInputRef}
                    type="text"
                    value={contractFormData.contractName}
                    onChange={(e) => {
                      const v = e.target.value;
                      setContractFormData((prev) => ({
                        ...prev,
                        contractName: v,
                      }));
                      setContractFieldErrors((prev) => {
                        if (!prev.contractName) {
                          return prev;
                        }
                        const next = { ...prev };
                        delete next.contractName;
                        return next;
                      });
                    }}
                    placeholder="Enter contract name"
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                  />
                  {contractFieldErrors.contractName ? (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400" role="alert">
                      {contractFieldErrors.contractName}
                    </p>
                  ) : null}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Sub Contract
                  </label>
                  <input
                    type="text"
                    value={contractFormData.subContract}
                    onChange={(e) =>
                      setContractFormData((prev) => ({
                        ...prev,
                        subContract: e.target.value,
                      }))
                    }
                    placeholder="Enter sub contract"
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Contract Version
                  </label>
                  <select
                    value={contractFormData.contractVersion}
                    onChange={(e) =>
                      setContractFormData((prev) => ({
                        ...prev,
                        contractVersion: e.target.value,
                      }))
                    }
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                  >
                    {CONTRACT_VERSION_SELECT_OPTIONS.map((ver) => (
                      <option key={ver} value={ver}>
                        {ver}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Effective Date *
                  </label>
                  <input
                    ref={contractEffectiveDateInputRef}
                    type="date"
                    value={contractFormData.effectiveDate}
                    onChange={(e) => {
                      const v = e.target.value;
                      setContractFormData((prev) => ({
                        ...prev,
                        effectiveDate: v,
                      }));
                      setContractFieldErrors((prev) => {
                        if (!prev.effectiveDate) {
                          return prev;
                        }
                        const next = { ...prev };
                        delete next.effectiveDate;
                        return next;
                      });
                    }}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                  />
                  {contractFieldErrors.effectiveDate ? (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400" role="alert">
                      {contractFieldErrors.effectiveDate}
                    </p>
                  ) : null}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={contractFormData.endDate}
                    onChange={(e) =>
                      setContractFormData((prev) => ({ ...prev, endDate: e.target.value }))
                    }
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Status *
                  </label>
                  <select
                    value={contractFormData.status}
                    onChange={(e) =>
                      setContractFormData((prev) => ({ ...prev, status: e.target.value }))
                    }
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                  >
                    <option value="Draft">Draft</option>
                    <option value="Active">Active</option>
                    <option value="Expired">Expired</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Billing Cycle *
                  </label>
                  <select
                    value={String(contractFormData.billingCycle ?? "")}
                    onChange={(e) => {
                      const v = e.target.value;
                      setContractFormData((prev) => ({ ...prev, billingCycle: v }));
                      setContractFieldErrors((prev) => {
                        if (!prev.billingCycle) {
                          return prev;
                        }
                        const next = { ...prev };
                        delete next.billingCycle;
                        return next;
                      });
                    }}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                  >
                    {CONTRACT_BILLING_CYCLE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  {contractFieldErrors.billingCycle ? (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400" role="alert">
                      {contractFieldErrors.billingCycle}
                    </p>
                  ) : null}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Net Terms *
                  </label>
                  <select
                    value={String(contractFormData.netTerms ?? "")}
                    onChange={(e) => {
                      const v = parseInt(e.target.value, 10);
                      setContractFormData((prev) => ({ ...prev, netTerms: v }));
                      setContractFieldErrors((prev) => {
                        if (!prev.netTerms) {
                          return prev;
                        }
                        const next = { ...prev };
                        delete next.netTerms;
                        return next;
                      });
                    }}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                  >
                    {CONTRACT_NET_TERMS_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  {contractFieldErrors.netTerms ? (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400" role="alert">
                      {contractFieldErrors.netTerms}
                    </p>
                  ) : null}
                </div>
              </div>

              <div className="flex items-center gap-4 mb-6 pb-6 border-b border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => setContractActionUnavailable("upload")}
                  className="app-btn-gloss"
                >
                  <Upload className="w-4 h-4" />
                  Upload Contract
                </button>
                <button
                  type="button"
                  disabled={!editingContract?.id}
                  title={
                    !editingContract?.id
                      ? "Save the contract, then use Edit on a saved contract to add services to the database."
                      : undefined
                  }
                  onClick={() => {
                    setServiceSaveSuccessBanner(null);
                    resetServicePanelForm();
                    setEditingServiceId(null);
                    setShowAddServiceDrawer(true);
                  }}
                  className="app-btn-gloss disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Plus className="w-4 h-4" />
                  Add Service
                </button>
                <button
                  type="button"
                  onClick={handleSaveContract}
                  disabled={
                    isCreatingContract ||
                    isUpdatingContract ||
                    (editingContract != null && !hasContractFormChanges)
                  }
                  title={
                    editingContract != null && !hasContractFormChanges
                      ? "Change a field before updating the contract."
                      : undefined
                  }
                  className="app-btn-gloss disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Save className="w-4 h-4" />
                  {editingContract
                    ? isUpdatingContract
                      ? "Updating…"
                      : "Update Contract"
                    : isCreatingContract
                      ? "Saving…"
                      : "Save Contract"}
                </button>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Uploaded Document
                </label>
                {contractFormData.uploadedDocument ? (
                  <div className="flex items-center gap-2 text-sm text-gray-900 dark:text-gray-100">
                    <FileText className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    <span>{contractFormData.uploadedDocument}</span>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                    No document uploaded
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Notes / Special Terms
                </label>
                <textarea
                  value={contractFormData.notes}
                  onChange={(e) =>
                    setContractFormData((prev) => ({ ...prev, notes: e.target.value }))
                  }
                  placeholder="Enter special terms, conditions, or notes"
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                />
              </div>
            </div>

            <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded shadow-sm">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0 flex-1">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                    Services &amp; Rates
                  </h2>
                  {isContractServicesError ? (
                    <p className="mt-2 text-sm text-red-600 dark:text-red-400" role="alert">
                      Could not load contract services from the server.
                    </p>
                  ) : null}
                  {editingContract?.id && isContractServicesLoading ? (
                    <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">Loading services…</p>
                  ) : null}
                  {!editingContract?.id ? (
                    <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                      Save the contract, then use <span className="font-medium">Edit</span> on a saved contract to add
                      or view services (stored in{" "}
                      <span className="font-mono text-xs">hgappact.act_contract_service</span>).
                    </p>
                  ) : null}
                </div>
                {editingContract?.id ? (
                  <div className="flex shrink-0 items-center gap-2 sm:pt-0.5">
                    <label
                      htmlFor="services-status-filter"
                      className="text-sm font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap"
                    >
                      Filter
                    </label>
                    <select
                      id="services-status-filter"
                      value={servicesStatusFilter}
                      onChange={(e) => {
                        setServicesStatusFilter(e.target.value);
                        setExpandedServiceId(null);
                      }}
                      className="min-w-[10rem] px-3 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                    >
                      {SERVICE_STATUS_FILTER_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : null}
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider w-10" />
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                        Hibbert Service Code
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                        Category
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                        Sub-category
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                        Services Rendered
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                        Based On
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                        Client Rate
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                        Effective Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                        End Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
                    {contractServices.length === 0 ? (
                      <tr>
                        <td
                          colSpan={10}
                          className="px-6 py-8 text-center text-sm text-gray-500 dark:text-gray-400"
                        >
                          No services in <span className="font-mono text-xs">hgappact.act_contract_service</span> for
                          this contract yet. Click &quot;Add Service&quot; to add one.
                        </td>
                      </tr>
                    ) : filteredContractServices.length === 0 ? (
                      <tr>
                        <td
                          colSpan={10}
                          className="px-6 py-8 text-center text-sm text-gray-500 dark:text-gray-400"
                        >
                          No services match the &quot;{servicesStatusFilter}&quot; filter.
                        </td>
                      </tr>
                    ) : (
                      filteredContractServices.map((service) => {
                        const canExpandService = serviceHasExpandableDetails(service);
                        return (
                        <Fragment key={service.id}>
                          <tr className="hover:bg-gray-50 dark:hover:bg-gray-800">
                            <td className="px-6 py-4 text-sm">
                              {canExpandService ? (
                                <button
                                  type="button"
                                  onClick={() =>
                                    setExpandedServiceId(
                                      expandedServiceId === service.id ? null : service.id
                                    )
                                  }
                                  className="app-btn-plain p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
                                >
                                  {expandedServiceId === service.id ? (
                                    <ChevronUp className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                                  ) : (
                                    <ChevronDown className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                                  )}
                                </button>
                              ) : null}
                            </td>
                            <td className="px-6 py-4 text-sm font-mono text-gray-900 dark:text-gray-100">
                              {service.hibbertServiceCode}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                              {service.category || "—"}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                              {service.subCategory || "—"}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                              {service.servicesRendered}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                              {service.basedOn || "—"}
                            </td>
                            <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-gray-100">
                              {serviceHasTieredPricing(service)
                                ? "Tiered Pricing"
                                : formatContractRateDisplay(service.rate) || "—"}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                              {service.effectiveDate}
                            </td>
                            <td className="px-6 py-4 text-sm">
                              <ServiceEndDateCell
                                endDate={service.endDate}
                                appliedByDisplayName={
                                  serviceHasEndDate(service)
                                    ? loggedInEmployeeDisplayName || service.endAppliedByDisplayName
                                    : ""
                                }
                              />
                            </td>
                            <td className="px-6 py-4 text-sm">
                              <div className="flex items-center gap-2">
                                {!serviceHasEndDate(service) ? (
                                  <button
                                    type="button"
                                    onClick={() => handleEditService(service.id)}
                                    className="app-btn-plain p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                                    title="Edit Service"
                                  >
                                    <Edit className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                  </button>
                                ) : null}
                                {!serviceHasEndDate(service) ? (
                                  <button
                                    type="button"
                                    onClick={() => handleDeleteService(service.id)}
                                    className="app-btn-plain p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                                    title="End service"
                                  >
                                    <Trash2 className="w-4 h-4 text-red-600 dark:text-red-400" />
                                  </button>
                                ) : null}
                              </div>
                            </td>
                          </tr>

                          {canExpandService && expandedServiceId === service.id ? (
                            <tr className="bg-gray-50 dark:bg-gray-800">
                              <td colSpan={10} className="px-6 py-4">
                                {service.notes ? (
                                  <p className="text-xs text-gray-700 dark:text-gray-300 mb-4">
                                    <span className="font-semibold">Notes: </span>
                                    {service.notes}
                                  </p>
                                ) : null}
                                {serviceHasTieredPricing(service) ||
                                isShippingFeeServiceCode(service.hibbertServiceCode) ? (
                                  <div
                                    className={`grid gap-6 ${
                                      serviceHasTieredPricing(service) &&
                                      isShippingFeeServiceCode(service.hibbertServiceCode)
                                        ? "grid-cols-2"
                                        : "grid-cols-1"
                                    }`}
                                  >
                                    {serviceHasTieredPricing(service) ? (
                                      <div>
                                        <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
                                          Tiered Pricing
                                        </h4>
                                        <div className="space-y-2">
                                          <div className="grid grid-cols-3 gap-2 text-xs font-medium text-gray-700 dark:text-gray-300">
                                            <span>Starting Qty</span>
                                            <span>Ending Qty</span>
                                            <span>Rate</span>
                                          </div>
                                          {(service.tieredPricing || []).map((tier) => (
                                            <div
                                              key={tier.id}
                                              className="grid grid-cols-3 gap-2 text-xs text-gray-900 dark:text-gray-100"
                                            >
                                              <span>{tier.fromVolume}</span>
                                              <span>{tier.toVolume}</span>
                                              <span className="font-medium">
                                                {formatContractRateDisplay(tier.rate) || "—"}
                                              </span>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    ) : null}

                                    {isShippingFeeServiceCode(service.hibbertServiceCode) ? (
                                      <div>
                                        <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
                                          Freight Billing
                                        </h4>
                                        <div className="space-y-3">
                                          <div>
                                            <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                              Provider
                                            </p>
                                            <p className="text-xs text-gray-900 dark:text-gray-100">
                                              {service.provider || "—"}
                                            </p>
                                          </div>
                                          <div>
                                            <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                              Options
                                            </p>
                                            <p className="text-xs text-gray-900 dark:text-gray-100">
                                              {service.pricingOption || "—"}
                                            </p>
                                          </div>
                                          {isShippingPricingOptionWithPercentage(service.pricingOption) ? (
                                            <div>
                                              <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                Freight Adjustment Percentage
                                              </p>
                                              <p className="text-xs text-gray-900 dark:text-gray-100">
                                                {String(service.freightAdjustmentPercentage ?? "").trim()
                                                  ? `${String(service.freightAdjustmentPercentage).trim()}%`
                                                  : "—"}
                                              </p>
                                            </div>
                                          ) : null}
                                        </div>
                                      </div>
                                    ) : null}
                                  </div>
                                ) : null}
                              </td>
                            </tr>
                          ) : null}
                        </Fragment>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
              </>
            ) : null}
          </div>
        )}

        {showAddServiceDrawer && (
          <div className="fixed inset-0 z-50 overflow-hidden">
            <div
              className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={handleCloseServiceDrawer}
              role="presentation"
            />

            <div className="fixed inset-y-0 right-0 max-w-2xl w-full flex">
              <div className="relative w-full bg-white dark:bg-gray-900 shadow-xl flex flex-col">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {editingServiceId ? "Edit Service" : "Add Service"}
                  </h3>
                  <button
                    type="button"
                    onClick={handleCloseServiceDrawer}
                    className="app-btn-plain rounded p-2 text-gray-600 transition-colors hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
                  >
                    <X className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                  </button>
                </div>

                <div className="flex-1 overflow-y-auto p-6">
                  <div className="space-y-6">
                    {serviceSaveSuccessBanner ? (
                      <div
                        className="rounded border border-green-300 bg-green-50 px-4 py-3 text-sm text-green-900 dark:border-green-800 dark:bg-green-950/40 dark:text-green-100 flex items-start justify-between gap-3"
                        role="status"
                      >
                        <p className="font-medium">{serviceSaveSuccessBanner}</p>
                        <button
                          type="button"
                          onClick={() => setServiceSaveSuccessBanner(null)}
                          className="app-btn-plain p-1 rounded hover:bg-green-100 dark:hover:bg-green-900/50 text-green-800 dark:text-green-200 shrink-0"
                          aria-label="Dismiss success message"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : null}
                    {serviceFieldErrors._global ? (
                      <p className="text-sm text-red-600 dark:text-red-400" role="alert">
                        {serviceFieldErrors._global}
                      </p>
                    ) : null}
                    {isServiceCatalogError ? (
                      <p className="text-sm text-red-600 dark:text-red-400" role="alert">
                        Service catalog could not be loaded. Try closing and reopening this panel.
                      </p>
                    ) : null}
                    {isServiceCatalogLoading ? (
                      <p className="text-sm text-gray-600 dark:text-gray-400">Loading service catalog…</p>
                    ) : null}

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Category *
                        </label>
                        <select
                          ref={serviceCatalogCategorySelectRef}
                          value={serviceCatalogCategoryFilter}
                          onChange={(e) => {
                            setServiceCatalogCategoryFilter(e.target.value);
                            setServiceCatalogSubCategoryFilter("");
                            setServiceFieldErrors((prev) => {
                              if (!prev.category) {
                                return prev;
                              }
                              const next = { ...prev };
                              delete next.category;
                              return next;
                            });
                          }}
                          disabled={isServiceCatalogLoading}
                          aria-invalid={Boolean(serviceFieldErrors.category)}
                          className={`w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 disabled:opacity-60 ${
                            serviceFieldErrors.category
                              ? "border-red-500 dark:border-red-500 focus:ring-red-500 focus:border-red-500"
                              : "border-gray-300 dark:border-gray-700 focus:ring-blue-600"
                          }`}
                        >
                          <option value="">Select category</option>
                          {serviceCatalogCategoryOptions.map((c) => (
                            <option key={c} value={c}>
                              {c}
                            </option>
                          ))}
                        </select>
                        {serviceFieldErrors.category ? (
                          <p className="mt-1 text-sm text-red-600 dark:text-red-400" role="alert">
                            {serviceFieldErrors.category}
                          </p>
                        ) : null}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Sub-category *
                        </label>
                        <select
                          ref={serviceCatalogSubCategorySelectRef}
                          value={serviceCatalogSubCategoryFilter}
                          onChange={(e) => {
                            setServiceCatalogSubCategoryFilter(e.target.value);
                            setServiceFieldErrors((prev) => {
                              if (!prev.subCategory) {
                                return prev;
                              }
                              const next = { ...prev };
                              delete next.subCategory;
                              return next;
                            });
                          }}
                          disabled={isServiceCatalogLoading || !serviceCatalogCategoryFilter}
                          aria-invalid={Boolean(serviceFieldErrors.subCategory)}
                          className={`w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 disabled:opacity-60 ${
                            serviceFieldErrors.subCategory
                              ? "border-red-500 dark:border-red-500 focus:ring-red-500 focus:border-red-500"
                              : "border-gray-300 dark:border-gray-700 focus:ring-blue-600"
                          }`}
                        >
                          {!serviceCatalogCategoryFilter ? (
                            <option value="">Select a category first</option>
                          ) : (
                            [
                              <option key="__all_sub_categories" value="">
                                Select sub-category
                              </option>,
                              ...serviceCatalogSubCategoryOptions.map((s) => (
                                <option key={s} value={s}>
                                  {s}
                                </option>
                              )),
                            ]
                          )}
                        </select>
                        {serviceFieldErrors.subCategory ? (
                          <p className="mt-1 text-sm text-red-600 dark:text-red-400" role="alert">
                            {serviceFieldErrors.subCategory}
                          </p>
                        ) : null}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Hibbert Service Code *
                      </label>
                      <select
                        ref={serviceHibbertCodeSelectRef}
                        value={serviceFormData.hibbertServiceCode}
                        onChange={(e) => {
                          const v = e.target.value;
                          const matched = serviceCatalogRows.find((r) => r.serviceCode === v);
                          const basedOnNext =
                            v.trim() === "" ? "" : String(matched?.unitOfMeasure ?? "").trim();
                          setServiceFormData((prev) => ({
                            ...prev,
                            hibbertServiceCode: v,
                            basedOn: basedOnNext,
                            provider: isShippingFeeServiceCode(v) ? prev.provider : "",
                            pricingOption: isShippingFeeServiceCode(v) ? prev.pricingOption : "",
                            freightAdjustmentPercentage: isShippingFeeServiceCode(v)
                              ? prev.freightAdjustmentPercentage
                              : "",
                          }));
                          setServiceFieldErrors((prev) => {
                            if (!prev.hibbertServiceCode && !prev.serviceCode) {
                              return prev;
                            }
                            const next = { ...prev };
                            delete next.hibbertServiceCode;
                            delete next.serviceCode;
                            return next;
                          });
                        }}
                        disabled={isServiceCatalogLoading}
                        aria-invalid={Boolean(serviceFieldErrors.hibbertServiceCode)}
                        className={`w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 disabled:opacity-60 ${
                          serviceFieldErrors.hibbertServiceCode
                            ? "border-red-500 dark:border-red-500 focus:ring-red-500 focus:border-red-500"
                            : "border-gray-300 dark:border-gray-700 focus:ring-blue-600"
                        }`}
                      >
                        <option value="">Select from Service Catalog</option>
                        {orphanServiceCatalogOption ? (
                          <option value={orphanServiceCatalogOption.code}>
                            {orphanServiceCatalogOption.label}
                          </option>
                        ) : null}
                        {filteredServiceCatalogRows.map((row) => (
                          <option key={row.serviceCode} value={row.serviceCode}>
                            {row.serviceCode}
                            {row.description ? ` — ${row.description}` : ""}
                          </option>
                        ))}
                      </select>
                      {serviceFieldErrors.hibbertServiceCode ? (
                        <p className="mt-1 text-sm text-red-600 dark:text-red-400" role="alert">
                          {serviceFieldErrors.hibbertServiceCode}
                        </p>
                      ) : null}
                      {String(serviceFormData.hibbertServiceCode ?? "").trim() ? (
                        <div className="mt-2">
                          <HoverCard openDelay={80} closeDelay={120}>
                            <HoverCardTrigger asChild>
                              <span
                                className="inline-flex text-sm font-medium text-blue-600 underline decoration-blue-600/50 decoration-dotted underline-offset-2 cursor-help dark:text-blue-400 dark:decoration-blue-400/50"
                                tabIndex={0}
                              >
                                Description
                              </span>
                            </HoverCardTrigger>
                            <HoverCardContent
                              align="start"
                              side="bottom"
                              sideOffset={8}
                              collisionPadding={16}
                              className="z-[200] w-auto min-w-[14rem] max-w-md border-gray-200 bg-white p-3 text-left shadow-lg dark:border-gray-600 dark:bg-gray-900"
                            >
                              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                Description
                              </p>
                              <p className="text-sm text-gray-900 dark:text-gray-100 whitespace-pre-wrap">
                                {selectedHibbertServiceDescription || "No description available."}
                              </p>
                            </HoverCardContent>
                          </HoverCard>
                        </div>
                      ) : null}
                    </div>

                    {isShippingFeeServiceCode(serviceFormData.hibbertServiceCode) ? (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Provider
                        </label>
                        <select
                          ref={serviceProviderSelectRef}
                          value={serviceFormData.provider ?? ""}
                          onChange={(e) => {
                            const v = e.target.value;
                            setServiceFormData((prev) => ({
                              ...prev,
                              provider: v,
                              pricingOption: v.trim() ? prev.pricingOption : "",
                              freightAdjustmentPercentage: v.trim() ? prev.freightAdjustmentPercentage : "",
                            }));
                            setServiceFieldErrors((prev) => {
                              if (!prev.provider) {
                                return prev;
                              }
                              const next = { ...prev };
                              delete next.provider;
                              return next;
                            });
                          }}
                          aria-invalid={Boolean(serviceFieldErrors.provider)}
                          className={`w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 ${
                            serviceFieldErrors.provider
                              ? "border-red-500 dark:border-red-500 focus:ring-red-500 focus:border-red-500"
                              : "border-gray-300 dark:border-gray-700 focus:ring-blue-600"
                          }`}
                        >
                          <option value="">Select provider</option>
                          {SHIPPING_PROVIDER_OPTIONS.map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                        {serviceFieldErrors.provider ? (
                          <p className="mt-1 text-sm text-red-600 dark:text-red-400" role="alert">
                            {serviceFieldErrors.provider}
                          </p>
                        ) : null}
                      </div>
                    ) : null}

                    {isShippingFeeServiceCode(serviceFormData.hibbertServiceCode) &&
                    String(serviceFormData.provider ?? "").trim() ? (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Options *
                        </label>
                        <select
                          ref={servicePricingOptionSelectRef}
                          value={serviceFormData.pricingOption ?? ""}
                          onChange={(e) => {
                            const v = e.target.value;
                            setServiceFormData((prev) => ({
                              ...prev,
                              pricingOption: v,
                              freightAdjustmentPercentage: isShippingPricingOptionWithPercentage(v)
                                ? prev.freightAdjustmentPercentage
                                : "",
                            }));
                            setServiceFieldErrors((prev) => {
                              if (!prev.pricingOption && !prev.options) {
                                return prev;
                              }
                              const next = { ...prev };
                              delete next.pricingOption;
                              delete next.options;
                              return next;
                            });
                          }}
                          aria-invalid={Boolean(
                            serviceFieldErrors.pricingOption || serviceFieldErrors.options
                          )}
                          className={`w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 ${
                            serviceFieldErrors.pricingOption || serviceFieldErrors.options
                              ? "border-red-500 dark:border-red-500 focus:ring-red-500 focus:border-red-500"
                              : "border-gray-300 dark:border-gray-700 focus:ring-blue-600"
                          }`}
                        >
                          <option value="">Select option</option>
                          {SHIPPING_PRICING_OPTION_VALUES.map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                        {serviceFieldErrors.pricingOption || serviceFieldErrors.options ? (
                          <p className="mt-1 text-sm text-red-600 dark:text-red-400" role="alert">
                            {serviceFieldErrors.pricingOption || serviceFieldErrors.options}
                          </p>
                        ) : null}
                      </div>
                    ) : null}

                    {isShippingFeeServiceCode(serviceFormData.hibbertServiceCode) &&
                    String(serviceFormData.provider ?? "").trim() &&
                    isShippingPricingOptionWithPercentage(serviceFormData.pricingOption) ? (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Freight Adjustment Percentage *
                        </label>
                        <input
                          ref={serviceFreightAdjustmentPercentageRef}
                          type="text"
                          inputMode="decimal"
                          value={serviceFormData.freightAdjustmentPercentage ?? ""}
                          onChange={(e) => {
                            const v = sanitizeFreightAdjustmentPercentageInput(e.target.value);
                            setServiceFormData((prev) => ({
                              ...prev,
                              freightAdjustmentPercentage: v,
                            }));
                            setServiceFieldErrors((prev) => {
                              if (!prev.freightAdjustmentPercentage) {
                                return prev;
                              }
                              const next = { ...prev };
                              delete next.freightAdjustmentPercentage;
                              return next;
                            });
                          }}
                          onPaste={(e) => {
                            e.preventDefault();
                            const pasted = e.clipboardData.getData("text");
                            const v = sanitizeFreightAdjustmentPercentageInput(pasted);
                            setServiceFormData((prev) => ({
                              ...prev,
                              freightAdjustmentPercentage: v,
                            }));
                            setServiceFieldErrors((prev) => {
                              if (!prev.freightAdjustmentPercentage) {
                                return prev;
                              }
                              const next = { ...prev };
                              delete next.freightAdjustmentPercentage;
                              return next;
                            });
                          }}
                          placeholder="Enter percentage"
                          aria-invalid={Boolean(serviceFieldErrors.freightAdjustmentPercentage)}
                          className={`w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 ${
                            serviceFieldErrors.freightAdjustmentPercentage
                              ? "border-red-500 dark:border-red-500 focus:ring-red-500 focus:border-red-500"
                              : "border-gray-300 dark:border-gray-700 focus:ring-blue-600"
                          }`}
                        />
                        {serviceFieldErrors.freightAdjustmentPercentage ? (
                          <p className="mt-1 text-sm text-red-600 dark:text-red-400" role="alert">
                            {serviceFieldErrors.freightAdjustmentPercentage}
                          </p>
                        ) : null}
                      </div>
                    ) : null}

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Services Rendered *
                      </label>
                      <textarea
                        ref={serviceServicesRenderedRef}
                        value={serviceFormData.servicesRendered}
                        onChange={(e) => {
                          const v = e.target.value;
                          setServiceFormData((prev) => ({
                            ...prev,
                            servicesRendered: v,
                          }));
                          setServiceFieldErrors((prev) => {
                            if (!prev.servicesRendered) {
                              return prev;
                            }
                            const next = { ...prev };
                            delete next.servicesRendered;
                            return next;
                          });
                        }}
                        placeholder="Describe the services rendered"
                        rows={3}
                        aria-invalid={Boolean(serviceFieldErrors.servicesRendered)}
                        className={`w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 ${
                          serviceFieldErrors.servicesRendered
                            ? "border-red-500 dark:border-red-500 focus:ring-red-500 focus:border-red-500"
                            : "border-gray-300 dark:border-gray-700 focus:ring-blue-600"
                        }`}
                      />
                      {serviceFieldErrors.servicesRendered ? (
                        <p className="mt-1 text-sm text-red-600 dark:text-red-400" role="alert">
                          {serviceFieldErrors.servicesRendered}
                        </p>
                      ) : null}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Tiered Pricing LOV
                      </label>
                      <select
                        ref={serviceTieredPricingLovRef}
                        value={serviceFormData.tieredPricingLov ?? TIERED_PRICING_LOV_NO}
                        onChange={(e) => {
                          const v = e.target.value;
                          setServiceFormData((prev) => ({
                            ...prev,
                            tieredPricingLov: v,
                            ...(v === TIERED_PRICING_LOV_NO
                              ? {
                                  tieredPricingTiers: [createEmptyTierPricingRow()],
                                  rate: prev.rate === "0" ? "" : prev.rate,
                                }
                              : {
                                  rate: "0",
                                  tieredPricingTiers: normalizeTierStartingQtyChain(
                                    Array.isArray(prev.tieredPricingTiers) &&
                                      prev.tieredPricingTiers.length > 0
                                      ? prev.tieredPricingTiers
                                      : [createEmptyTierPricingRow()]
                                  ),
                                }),
                          }));
                          setServiceFieldErrors((prev) => {
                            const next = { ...prev };
                            delete next.tieredPricingLov;
                            delete next.tieredPricingTiers;
                            delete next.rate;
                            return next;
                          });
                        }}
                        aria-invalid={Boolean(serviceFieldErrors.tieredPricingLov)}
                        className={`w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 ${
                          serviceFieldErrors.tieredPricingLov
                            ? "border-red-500 dark:border-red-500 focus:ring-red-500 focus:border-red-500"
                            : "border-gray-300 dark:border-gray-700 focus:ring-blue-600"
                        }`}
                      >
                        <option value={TIERED_PRICING_LOV_NO}>{TIERED_PRICING_LOV_NO}</option>
                        <option value={TIERED_PRICING_LOV_YES}>{TIERED_PRICING_LOV_YES}</option>
                      </select>
                      {serviceFieldErrors.tieredPricingLov ? (
                        <p className="mt-1 text-sm text-red-600 dark:text-red-400" role="alert">
                          {serviceFieldErrors.tieredPricingLov}
                        </p>
                      ) : null}
                    </div>

                    {serviceFormData.tieredPricingLov === TIERED_PRICING_LOV_YES ? (
                      <div className="space-y-4 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                        {(serviceFormData.tieredPricingTiers ?? []).map((tier, tierIndex) => {
                          const tierRowErrors = Array.isArray(serviceFieldErrors.tieredPricingTiers)
                            ? serviceFieldErrors.tieredPricingTiers[tierIndex]
                            : null;
                          const tierCount = serviceFormData.tieredPricingTiers?.length ?? 0;
                          const startingQtyDisplay =
                            tierIndex === 0
                              ? FIRST_TIER_STARTING_QTY
                              : tier.startingQty;
                          const currentTierHasErrors =
                            validateSingleTierRowErrors(tier, tierIndex) != null;
                          return (
                            <div
                              key={tier.id}
                              className={
                                tierIndex < tierCount - 1
                                  ? "pb-4 border-b border-gray-200 dark:border-gray-700"
                                  : ""
                              }
                            >
                              {tierCount > 1 ? (
                                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-3">
                                  Tier {tierIndex + 1}
                                </p>
                              ) : null}
                              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Starting Qty *
                                  </label>
                                  <input
                                    type="text"
                                    inputMode="numeric"
                                    data-tier-field="startingQty"
                                    data-tier-index={tierIndex}
                                    value={startingQtyDisplay}
                                    readOnly
                                    aria-readonly
                                    placeholder={
                                      tierIndex === 0
                                        ? FIRST_TIER_STARTING_QTY
                                        : "Set prior tier ending qty"
                                    }
                                    aria-invalid={Boolean(tierRowErrors?.startingQty)}
                                    onBlur={
                                      tierIndex > 0
                                        ? () => {
                                            setServiceFormData((prev) => {
                                              validateTierFieldsOnBlur(
                                                setServiceFieldErrors,
                                                prev.tieredPricingTiers ?? [],
                                                [tierIndex],
                                                {
                                                  preferTierIndex: tierIndex,
                                                  preferField: "startingQty",
                                                }
                                              );
                                              return prev;
                                            });
                                          }
                                        : undefined
                                    }
                                    className={`w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 text-gray-900 dark:text-gray-100 bg-gray-100 dark:bg-gray-800 cursor-not-allowed border-gray-300 dark:border-gray-700 ${
                                      tierRowErrors?.startingQty
                                        ? "border-red-500 dark:border-red-500"
                                        : ""
                                    }`}
                                  />
                                  <button
                                    type="button"
                                    disabled={currentTierHasErrors}
                                    onClick={() => {
                                      addContractServiceTierAfterIndex(
                                        setServiceFormData,
                                        setServiceFieldErrors,
                                        serviceFormData.tieredPricingTiers ?? [],
                                        tierIndex
                                      );
                                    }}
                                    className={`app-btn-gloss mt-2 inline-flex items-center gap-1 px-2 py-1 text-xs ${
                                      currentTierHasErrors ? "cursor-not-allowed opacity-50" : ""
                                    }`}
                                    title={
                                      currentTierHasErrors
                                        ? "Complete this tier before adding another"
                                        : "Add another tier"
                                    }
                                    aria-label={`Add tier after tier ${tierIndex + 1}`}
                                    aria-disabled={currentTierHasErrors}
                                  >
                                    <Plus className="w-3.5 h-3.5" />
                                    Add tier
                                  </button>
                                  {tierRowErrors?.startingQty ? (
                                    <p
                                      className="mt-1 text-sm text-red-600 dark:text-red-400"
                                      role="alert"
                                    >
                                      {tierRowErrors.startingQty}
                                    </p>
                                  ) : null}
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Ending Qty *
                                  </label>
                                  <input
                                    type="text"
                                    inputMode="numeric"
                                    data-tier-field="endingQty"
                                    data-tier-index={tierIndex}
                                    value={tier.endingQty}
                                    onChange={(e) => {
                                      const v = e.target.value;
                                      setServiceFormData((prev) => {
                                        const updated = (prev.tieredPricingTiers ?? []).map(
                                          (row, i) =>
                                            i === tierIndex ? { ...row, endingQty: v } : row
                                        );
                                        return {
                                          ...prev,
                                          tieredPricingTiers: cascadeTierStartingQtyAfterEndingChange(
                                            updated,
                                            tierIndex
                                          ),
                                        };
                                      });
                                      clearTieredPricingTierFieldError(
                                        setServiceFieldErrors,
                                        tierIndex,
                                        "endingQty"
                                      );
                                    }}
                                    onBlur={() => {
                                      setServiceFormData((prev) => {
                                        const tiers = prev.tieredPricingTiers ?? [];
                                        const indexes = [tierIndex];
                                        if (tierIndex + 1 < tiers.length) {
                                          indexes.push(tierIndex + 1);
                                        }
                                        validateTierFieldsOnBlur(
                                          setServiceFieldErrors,
                                          tiers,
                                          indexes,
                                          {
                                            preferTierIndex: tierIndex,
                                            preferField: "endingQty",
                                            limitToIndexes: indexes,
                                          }
                                        );
                                        return prev;
                                      });
                                    }}
                                    placeholder="e.g. 100"
                                    aria-invalid={Boolean(tierRowErrors?.endingQty)}
                                    className={`w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 ${
                                      tierRowErrors?.endingQty
                                        ? "border-red-500 dark:border-red-500 focus:ring-red-500 focus:border-red-500"
                                        : "border-gray-300 dark:border-gray-700 focus:ring-blue-600"
                                    }`}
                                  />
                                  {tierRowErrors?.endingQty ? (
                                    <p
                                      className="mt-1 text-sm text-red-600 dark:text-red-400"
                                      role="alert"
                                    >
                                      {tierRowErrors.endingQty}
                                    </p>
                                  ) : null}
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Rate *
                                  </label>
                                  <input
                                    type="text"
                                    data-tier-field="tierRate"
                                    data-tier-index={tierIndex}
                                    value={tier.tierRate}
                                    onChange={(e) => {
                                      const v = e.target.value;
                                      setServiceFormData((prev) => ({
                                        ...prev,
                                        tieredPricingTiers: (prev.tieredPricingTiers ?? []).map(
                                          (row, i) =>
                                            i === tierIndex ? { ...row, tierRate: v } : row
                                        ),
                                      }));
                                      clearTieredPricingTierFieldError(
                                        setServiceFieldErrors,
                                        tierIndex,
                                        "tierRate"
                                      );
                                    }}
                                    onBlur={() => {
                                      setServiceFormData((prev) => {
                                        const tiers = [...(prev.tieredPricingTiers ?? [])];
                                        const row = tiers[tierIndex];
                                        let changed = false;
                                        if (row) {
                                          const formatted = formatTierRateForDisplay(row.tierRate);
                                          if (formatted !== row.tierRate) {
                                            tiers[tierIndex] = { ...row, tierRate: formatted };
                                            changed = true;
                                          }
                                        }
                                        validateTierFieldsOnBlur(
                                          setServiceFieldErrors,
                                          tiers,
                                          [tierIndex],
                                          {
                                            preferTierIndex: tierIndex,
                                            preferField: "tierRate",
                                          }
                                        );
                                        return changed
                                          ? { ...prev, tieredPricingTiers: tiers }
                                          : prev;
                                      });
                                    }}
                                    placeholder="e.g. 10.89 or $1,234.56 (max 2 decimals)"
                                    inputMode="decimal"
                                    aria-invalid={Boolean(tierRowErrors?.tierRate)}
                                    className={`w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 ${
                                      tierRowErrors?.tierRate
                                        ? "border-red-500 dark:border-red-500 focus:ring-red-500 focus:border-red-500"
                                        : "border-gray-300 dark:border-gray-700 focus:ring-blue-600"
                                    }`}
                                  />
                                  {tierRowErrors?.tierRate ? (
                                    <p
                                      className="mt-1 text-sm text-red-600 dark:text-red-400"
                                      role="alert"
                                    >
                                      {tierRowErrors.tierRate}
                                    </p>
                                  ) : null}
                                </div>
                              </div>
                              {tierIndex === tierCount - 1 && tierCount > 1 ? (
                                <div className="mt-3">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setServiceFormData((prev) => {
                                        const list = [...(prev.tieredPricingTiers ?? [])];
                                        if (list.length <= 1) {
                                          return prev;
                                        }
                                        list.pop();
                                        return { ...prev, tieredPricingTiers: list };
                                      });
                                      setServiceFieldErrors((prev) => {
                                        if (!prev.tieredPricingTiers) {
                                          return prev;
                                        }
                                        const next = { ...prev };
                                        delete next.tieredPricingTiers;
                                        return next;
                                      });
                                    }}
                                    className="app-btn-gloss inline-flex items-center gap-1 px-2 py-1 text-xs"
                                    title="Remove last tier"
                                    aria-label="Remove last tier"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                    Remove
                                  </button>
                                </div>
                              ) : null}
                            </div>
                          );
                        })}
                      </div>
                    ) : null}

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Unit of Measure
                      </label>
                      <select
                        ref={serviceBasedOnRef}
                        value={serviceFormData.basedOn}
                        onChange={(e) => {
                          const v = e.target.value;
                          setServiceFormData((prev) => ({ ...prev, basedOn: v }));
                          setServiceFieldErrors((prev) => {
                            if (!prev.basedOn) {
                              return prev;
                            }
                            const next = { ...prev };
                            delete next.basedOn;
                            return next;
                          });
                        }}
                        disabled={isServiceCatalogLoading || serviceCatalogUnitOfMeasureOptions.length === 0}
                        aria-invalid={Boolean(serviceFieldErrors.basedOn)}
                        className={`w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 disabled:opacity-60 ${
                          serviceFieldErrors.basedOn
                            ? "border-red-500 dark:border-red-500 focus:ring-red-500 focus:border-red-500"
                            : "border-gray-300 dark:border-gray-700 focus:ring-blue-600"
                        }`}
                      >
                        <option value="">
                          {isServiceCatalogLoading
                            ? "Loading units of measure…"
                            : serviceCatalogUnitOfMeasureOptions.length === 0
                              ? "No units of measure in catalog"
                              : "Select unit of measure"}
                        </option>
                        {serviceCatalogUnitOfMeasureOptions.map((uom) => (
                          <option key={uom} value={uom}>
                            {uom}
                          </option>
                        ))}
                      </select>
                      {serviceFieldErrors.basedOn ? (
                        <p className="mt-1 text-sm text-red-600 dark:text-red-400" role="alert">
                          {serviceFieldErrors.basedOn}
                        </p>
                      ) : null}
                    </div>

                    {serviceFormData.tieredPricingLov !== TIERED_PRICING_LOV_YES ? (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Client Rate *
                        </label>
                        <input
                          ref={serviceRateInputRef}
                          type="text"
                          value={serviceFormData.rate}
                          onChange={(e) => {
                            const v = e.target.value;
                            setServiceFormData((prev) => ({ ...prev, rate: v }));
                            setServiceFieldErrors((prev) => {
                              if (!prev.rate) {
                                return prev;
                              }
                              const next = { ...prev };
                              delete next.rate;
                              return next;
                            });
                          }}
                          placeholder="e.g. 10.89 or $1,234.56 (max 2 decimals)"
                          aria-invalid={Boolean(serviceFieldErrors.rate)}
                          className={`w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 ${
                            serviceFieldErrors.rate
                              ? "border-red-500 dark:border-red-500 focus:ring-red-500 focus:border-red-500"
                              : "border-gray-300 dark:border-gray-700 focus:ring-blue-600"
                          }`}
                        />
                        {serviceFieldErrors.rate ? (
                          <p className="mt-1 text-sm text-red-600 dark:text-red-400" role="alert">
                            {serviceFieldErrors.rate}
                          </p>
                        ) : null}
                      </div>
                    ) : null}

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Effective Date *
                        </label>
                        <input
                          ref={serviceEffectiveDateInputRef}
                          type="date"
                          value={serviceFormData.effectiveDate}
                          onChange={(e) => {
                            const v = e.target.value;
                            setServiceFormData((prev) => ({
                              ...prev,
                              effectiveDate: v,
                            }));
                            setServiceFieldErrors((prev) => {
                              if (!prev.effectiveDate) {
                                return prev;
                              }
                              const next = { ...prev };
                              delete next.effectiveDate;
                              return next;
                            });
                          }}
                          aria-invalid={Boolean(serviceFieldErrors.effectiveDate)}
                          className={`w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 ${
                            serviceFieldErrors.effectiveDate
                              ? "border-red-500 dark:border-red-500 focus:ring-red-500 focus:border-red-500"
                              : "border-gray-300 dark:border-gray-700 focus:ring-blue-600"
                          }`}
                        />
                        {serviceFieldErrors.effectiveDate ? (
                          <p className="mt-1 text-sm text-red-600 dark:text-red-400" role="alert">
                            {serviceFieldErrors.effectiveDate}
                          </p>
                        ) : null}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          End Date
                        </label>
                        <input
                          type="date"
                          value={serviceFormData.endDate ?? ""}
                          onChange={(e) => {
                            const v = e.target.value;
                            setServiceFormData((prev) => ({
                              ...prev,
                              endDate: v,
                            }));
                            setServiceFieldErrors((prev) => {
                              if (!prev.endDate) {
                                return prev;
                              }
                              const next = { ...prev };
                              delete next.endDate;
                              return next;
                            });
                          }}
                          aria-invalid={Boolean(serviceFieldErrors.endDate)}
                          className={`w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 ${
                            serviceFieldErrors.endDate
                              ? "border-red-500 dark:border-red-500 focus:ring-red-500 focus:border-red-500"
                              : "border-gray-300 dark:border-gray-700 focus:ring-blue-600"
                          }`}
                        />
                        {serviceFieldErrors.endDate ? (
                          <p className="mt-1 text-sm text-red-600 dark:text-red-400" role="alert">
                            {serviceFieldErrors.endDate}
                          </p>
                        ) : null}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Notes
                      </label>
                      <textarea
                        ref={serviceNotesRef}
                        value={serviceFormData.notes}
                        onChange={(e) => {
                          const v = e.target.value;
                          setServiceFormData((prev) => ({ ...prev, notes: v }));
                          setServiceFieldErrors((prev) => {
                            if (!prev.notes) {
                              return prev;
                            }
                            const next = { ...prev };
                            delete next.notes;
                            return next;
                          });
                        }}
                        placeholder="Additional notes about this service"
                        rows={3}
                        aria-invalid={Boolean(serviceFieldErrors.notes)}
                        className={`w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 ${
                          serviceFieldErrors.notes
                            ? "border-red-500 dark:border-red-500 focus:ring-red-500 focus:border-red-500"
                            : "border-gray-300 dark:border-gray-700 focus:ring-blue-600"
                        }`}
                      />
                      {serviceFieldErrors.notes ? (
                        <p className="mt-1 text-sm text-red-600 dark:text-red-400" role="alert">
                          {serviceFieldErrors.notes}
                        </p>
                      ) : null}
                    </div>

                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                      <p className="text-xs text-blue-900 dark:text-blue-100">
                        Contract services and rates defined here are used in Billing Profiles and
                        Invoice Configuration.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-end gap-4">
                  <button
                    type="button"
                    onClick={handleCloseServiceDrawer}
                    className="app-btn-gloss-cancel px-6"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleAddService}
                    disabled={
                      isCreatingContractService ||
                      isUpdatingContractService ||
                      (editingServiceId != null && !hasServiceFormChanges)
                    }
                    title={
                      editingServiceId != null && !hasServiceFormChanges
                        ? "Change a field before updating the service."
                        : undefined
                    }
                    className="app-btn-gloss px-6 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {editingServiceId ? (
                      <>
                        <Save className="w-4 h-4" />
                        Update Service
                      </>
                    ) : (
                      <>
                        <Plus className="w-4 h-4" />
                        Add Service
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === "billing-profiles" && <div className="p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Invoice Profile Configuration</h2>
              {editingBillingProfileId == null ? (
                <button
                  type="button"
                  disabled={custIdForQuery == null}
                  onClick={() => {
                    setEditingBillingProfileId(null);
                    setShowCreateInvoiceProfileModal(true);
                  }}
                  className="app-btn-gloss shrink-0 disabled:cursor-not-allowed disabled:opacity-50"
                  title={
                    custIdForQuery == null
                      ? "Save the client before adding an invoice profile"
                      : undefined
                  }
                >
                  <Plus className="h-4 w-4" />
                  Add Invoice Profile
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setEditingBillingProfileId(null)}
                  className="app-btn-gloss-cancel shrink-0"
                >
                  Back to Profiles
                </button>
              )}
            </div>

            {editingBillingProfileId == null ? (
            <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded shadow-sm">
              <div className="flex flex-wrap items-center justify-end gap-4 border-b border-gray-200 px-6 py-3 dark:border-gray-700">
                <div className="flex items-center gap-2">
                  <label
                    htmlFor="billing-profile-status-filter"
                    className="text-sm font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap"
                  >
                    Status
                  </label>
                  <select
                    id="billing-profile-status-filter"
                    value={billingProfileStatusFilter}
                    onChange={(e) => setBillingProfileStatusFilter(e.target.value)}
                    className="min-w-[10rem] px-3 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                  >
                    {BILLING_PROFILE_STATUS_FILTER_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  <label
                    htmlFor="billing-profile-company-filter"
                    className="text-sm font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap"
                  >
                    Company #
                  </label>
                  <select
                    id="billing-profile-company-filter"
                    value={billingProfileCompanyFilter}
                    onChange={(e) => setBillingProfileCompanyFilter(e.target.value)}
                    className="min-w-[10rem] px-3 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                  >
                    {billingProfileCompanyFilterOptions.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                        Profile Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                        Company #
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
                    {isInvProfilesFetching && billingProfiles.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="px-6 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                          Loading invoice profiles…
                        </td>
                      </tr>
                    ) : null}
                    {!isInvProfilesFetching && billingProfiles.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="px-6 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                          No invoice profiles yet. Use Add Invoice Profile to create one.
                        </td>
                      </tr>
                    ) : null}
                    {!isInvProfilesFetching &&
                    billingProfiles.length > 0 &&
                    filteredBillingProfiles.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="px-6 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                          No invoice profiles match the &quot;
                          {billingProfileActiveFilterDescription(
                            billingProfileStatusFilter,
                            billingProfileCompanyFilter
                          )}
                          &quot; filter.
                        </td>
                      </tr>
                    ) : null}
                    {filteredBillingProfiles.map((profile) => (
                      <tr
                        key={profile.id}
                        className="hover:bg-gray-50 dark:hover:bg-gray-800"
                      >
                        <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-gray-100">
                          {profile.profileName}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                          {profile.companyNumber || "—"}
                        </td>
                        <td className="px-6 py-4 text-sm">
                          <span
                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              profile.activeFlag
                                ? "bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200"
                                : "bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200"
                            }`}
                          >
                            {profile.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm">
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => handleEditBillingProfile(profile.id)}
                              className="app-btn-plain p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                              title="Edit Profile"
                            >
                              <Edit className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            ) : null}

            {editingBillingProfileId != null && custIdForQuery != null ? (
              <div
                ref={billingProfileEditPanelRef}
                className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded shadow-sm p-6"
              >
                <BillingProfileConfiguration
                  embedded
                  custId={custIdForQuery}
                  clientName={contractPageClientName}
                  clientCode={(formData.code ?? "").trim()}
                  focusInvProfileId={editingBillingProfileId}
                  onCloseEmbedded={() => setEditingBillingProfileId(null)}
                />
              </div>
            ) : null}

            {showCreateInvoiceProfileModal && custIdForQuery != null ? (
              <BillingProfileConfiguration
                embedded
                createModalOnly
                createModalOpen={showCreateInvoiceProfileModal}
                onCreateModalOpenChange={setShowCreateInvoiceProfileModal}
                custId={custIdForQuery}
                clientName={contractPageClientName}
                clientCode={(formData.code ?? "").trim()}
              />
            ) : null}
          </div>}

        {activeTab === "invoice-config" && <div className="p-8">
            <InvoiceConfiguration
    invoiceLines={invoiceLines}
    onSave={handleSaveInvoiceLine}
    onEdit={handleEditInvoiceLine}
    onDelete={handleDeleteInvoiceLine}
  />
          </div>}
      </div>

      {contractActionUnavailable ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-8"
          role="dialog"
          aria-modal="true"
          aria-labelledby="contract-action-unavailable-title"
        >
          <button
            type="button"
            className="app-btn-plain absolute inset-0 bg-black/50 backdrop-blur-[1px]"
            aria-label="Close"
            onClick={() => setContractActionUnavailable(null)}
          />
          <div
            className="relative z-[1] w-full max-w-md rounded-xl border border-gray-200 bg-white p-6 shadow-2xl dark:border-gray-600 dark:bg-gray-900 dark:shadow-black/40"
            onClick={(e) => e.stopPropagation()}
          >
            <h2
              id="contract-action-unavailable-title"
              className="text-lg font-semibold text-gray-900 dark:text-gray-100"
            >
              {CONTRACT_ACTION_UNAVAILABLE_COPY[contractActionUnavailable].title}
            </h2>
            <p className="mt-3 text-sm text-gray-600 dark:text-gray-400">
              {CONTRACT_ACTION_UNAVAILABLE_COPY[contractActionUnavailable].message}
            </p>
            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={() => setContractActionUnavailable(null)}
                className="app-btn-plain px-4 py-2 border border-gray-300 dark:border-gray-700 rounded hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-gray-900 dark:text-gray-100"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {pendingDeleteContractId != null ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-gray-500 bg-opacity-50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-contract-dialog-title"
        >
          <div className="w-full max-w-md rounded border border-gray-200 bg-white p-8 shadow-xl dark:border-gray-700 dark:bg-gray-900">
            <h2
              id="delete-contract-dialog-title"
              className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3"
            >
              Delete contract?
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              {pendingDeleteContract?.contractName
                ? `Are you sure you want to delete "${pendingDeleteContract.contractName}"? This action cannot be undone.`
                : "Are you sure you want to delete this contract? This action cannot be undone."}
            </p>
            {deleteContractError ? (
              <p className="mb-4 text-sm text-red-600 dark:text-red-400" role="alert">
                {deleteContractError}
              </p>
            ) : null}
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={handleCancelDeleteContract}
                disabled={isDeletingContract}
                className="app-btn-gloss-cancel disabled:cursor-not-allowed disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmDeleteContract}
                disabled={isDeletingContract}
                className="app-btn-gloss disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isDeletingContract ? "Deleting…" : "Delete Contract"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {pendingDeleteServiceId != null ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-gray-500 bg-opacity-50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="end-service-dialog-title"
        >
          <div className="w-full max-w-md rounded border border-gray-200 bg-white p-8 shadow-xl dark:border-gray-700 dark:bg-gray-900">
            <h2
              id="end-service-dialog-title"
              className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3"
            >
              End service?
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Are you sure you want to end this service? This action cannot be undone.
            </p>
            {deleteServiceError ? (
              <p className="mb-4 text-sm text-red-600 dark:text-red-400" role="alert">
                {deleteServiceError}
              </p>
            ) : null}
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={handleCancelDeleteService}
                disabled={isEndingContractService}
                className="app-btn-gloss-cancel disabled:cursor-not-allowed disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmDeleteService}
                disabled={isEndingContractService}
                className="app-btn-gloss disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isEndingContractService ? "Saving…" : "Add End Date"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {showClientUpdatedPopup ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-gray-500 bg-opacity-50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="client-updated-dialog-title"
        >
          <div className="w-full max-w-md rounded border border-gray-200 bg-white p-8 shadow-xl dark:border-gray-700 dark:bg-gray-900">
            <h2
              id="client-updated-dialog-title"
              className="text-xl font-semibold text-gray-900 dark:text-gray-100"
            >
              Client updated successfully!
            </h2>
            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={handleDismissClientUpdatedPopup}
                className="app-btn-gloss px-6"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {
    /* PO Modal */
  }
      {showPOModal && <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex items-center justify-center">
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded shadow-sm p-8 w-96">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-6">Purchase Order Details</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  PO Number
                </label>
                <input
    type="text"
    value={poFormData.poNumber}
    onChange={(e) => setPoFormData((prev) => ({ ...prev, poNumber: e.target.value }))}
    placeholder="Enter PO number"
    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
  />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Start Date
                </label>
                <input
    type="date"
    value={poFormData.startDate}
    onChange={(e) => setPoFormData((prev) => ({ ...prev, startDate: e.target.value }))}
    placeholder="Enter start date"
    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
  />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  End Date
                </label>
                <input
    type="date"
    value={poFormData.endDate}
    onChange={(e) => setPoFormData((prev) => ({ ...prev, endDate: e.target.value }))}
    placeholder="Enter end date"
    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
  />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Status
                </label>
                <select
    value={poFormData.status}
    onChange={(e) => setPoFormData((prev) => ({ ...prev, status: e.target.value }))}
    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
  >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Amount
                </label>
                <input
    type="text"
    value={poFormData.amount}
    onChange={(e) => setPoFormData((prev) => ({ ...prev, amount: e.target.value }))}
    placeholder="Enter amount"
    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
  />
              </div>
            </div>
            <div className="flex items-center gap-4 pt-6 border-t border-gray-200 dark:border-gray-700">
              <button className="app-btn-gloss px-6" onClick={handleSavePO}>
                <Save className="w-4 h-4" />
                Save
              </button>
              <button className="app-btn-gloss-cancel px-6" onClick={() => setShowPOModal(false)}>
                <X className="w-4 h-4" />
                Cancel
              </button>
            </div>
          </div>
        </div>}
    </div>;
}
export {
  ClientConfiguration
};
